import * as i0 from '@angular/core';
import { InjectionToken, Injectable, isDevMode, SimpleChange, Optional, Inject, EventEmitter, Component, Input, Output, SecurityContext, NgModule, SkipSelf } from '@angular/core';
import { Observable, ReplaySubject, of, combineLatest } from 'rxjs';
import { mergeMap, tap, catchError, first, map } from 'rxjs/operators';
import * as i1 from '@angular/platform-browser';

/**
 * The default values for the OutletOptions
 */
const outletOptionDefaults = {
  sanitize: true,
  convertHTMLEntities: true,
  fixParagraphTags: true,
  updateOnPushOnly: false,
  compareInputsByValue: false,
  compareOutputsByValue: false,
  compareByValueDepth: 5,
  ignoreInputAliases: false,
  ignoreOutputAliases: false,
  acceptInputsForAnyProperty: false,
  acceptOutputsForAnyObservable: false
};

/**
 * Custom injector tokens that are used for varous internal communication purposes
 */
const DYNAMICHOOKS_FORROOTCALLED = new InjectionToken('A provider that is set in forRoot, so forChild can see if forRoot was called first');
const DYNAMICHOOKS_FORROOTCHECK = new InjectionToken('Inject this anywhere to check whether or not forRoot was called and throw an error if not');
const DYNAMICHOOKS_ALLSETTINGS = new InjectionToken('All of the settings registered in the whole app.');
const DYNAMICHOOKS_ANCESTORSETTINGS = new InjectionToken('The settings collected from all ancestor injectors');
const DYNAMICHOOKS_MODULESETTINGS = new InjectionToken('The settings for the currently loaded module.');
var DynamicHooksInheritance = /*#__PURE__*/function (DynamicHooksInheritance) {
  DynamicHooksInheritance[DynamicHooksInheritance["All"] = 0] = "All";
  DynamicHooksInheritance[DynamicHooksInheritance["Linear"] = 1] = "Linear";
  DynamicHooksInheritance[DynamicHooksInheritance["None"] = 2] = "None";
  return DynamicHooksInheritance;
}(DynamicHooksInheritance || {});
/**
 * A powerful parser for standard Angular component selectors that comes with this library
 */
class SelectorHookParser {
  constructor(config, configResolver, selectorFinder, bindingStateManager) {
    this.configResolver = configResolver;
    this.selectorFinder = selectorFinder;
    this.bindingStateManager = bindingStateManager;
    // Keep track of all hooks and their current bindings to keep references on updates
    this.currentBindings = {};
    this.config = this.configResolver.processConfig(config);
    this.name = this.config.name;
  }
  // Main parser functions
  // --------------------------------------------------------------------------
  findHooks(content, context) {
    return this.config.enclosing ? this.selectorFinder.findEnclosingSelectors(content, this.config.selector, this.config.bracketStyle) : this.selectorFinder.findStandaloneSelectors(content, this.config.selector, this.config.bracketStyle);
  }
  loadComponent(hookId, hookValue, context, childNodes) {
    return {
      component: this.config.component,
      injector: this.config.injector
    };
  }
  getBindings(hookId, hookValue, context) {
    if (!this.currentBindings.hasOwnProperty(hookId)) {
      this.currentBindings[hookId] = {};
    }
    // Store the bindings for each hook to preserve references in between updates
    const hookBindings = this.currentBindings[hookId];
    hookBindings.inputs = this.bindingStateManager.getCurrentInputBindings(hookValue.openingTag, context, this.config, hookBindings.inputs);
    hookBindings.outputs = this.bindingStateManager.getCurrentOutputBindings(hookValue.openingTag, this.config, hookBindings.outputs);
    return {
      inputs: this.getValuesFromBindings(hookBindings.inputs),
      outputs: this.getValuesFromBindings(hookBindings.outputs)
    };
  }
  // --------------------------------------------------------------------------
  /**
   * Transforms a RichBindingData object into a normal bindings object
   *
   * @param richBindingsObject - The object containing the RichBindingData
   */
  getValuesFromBindings(richBindingsObject) {
    const result = {};
    for (const [key, value] of Object.entries(richBindingsObject)) {
      result[key] = value.value;
    }
    return result;
  }
}

/**
 * The default values for the SelectorHookParserConfig
 */
const selectorHookParserConfigDefaults = {
  component: undefined,
  name: undefined,
  selector: undefined,
  injector: undefined,
  enclosing: true,
  bracketStyle: {
    opening: '<',
    closing: '>'
  },
  parseInputs: true,
  unescapeStrings: true,
  inputsBlacklist: null,
  inputsWhitelist: null,
  outputsBlacklist: null,
  outputsWhitelist: null,
  allowContextInBindings: true,
  allowContextFunctionCalls: true
};

/**
 * A helper class for resolving SelectorHookParserConfig
 */
let SelectorHookParserConfigResolver = /*#__PURE__*/(() => {
  class SelectorHookParserConfigResolver {
    constructor(cfr) {
      this.cfr = cfr;
    }
    /**
     * Overwrites the default parser config with a (partial) SelectorHookParserConfig object and returns the result
     *
     * @param userParserConfig - The (partial) SelectorHookParserConfig object
     */
    processConfig(userParserConfig) {
      const parserConfig = JSON.parse(JSON.stringify(selectorHookParserConfigDefaults));
      // component
      if (!userParserConfig || !userParserConfig.hasOwnProperty('component')) {
        throw Error('Missing the required "component" property for the SelectorHookParserConfig. Must be either the component class or a LazyLoadComponentConfig.');
      }
      parserConfig.component = userParserConfig.component;
      // If is class
      if (userParserConfig.component.hasOwnProperty('prototype')) {
        parserConfig.selector = this.cfr.resolveComponentFactory(userParserConfig.component).selector;
        // If is LazyLoadingComponentConfig
      } else if (userParserConfig.component.hasOwnProperty('importPromise') && userParserConfig.component.hasOwnProperty('importName')) {
        if (!userParserConfig.hasOwnProperty('selector')) {
          throw Error(`When using lazy-loaded dynamic components, you have to specify the "selector" property in the parser config (that will be used to find it in the text), as the real selector can't be known before the component is loaded.`);
        }
        // If is neither
      } else {
        throw Error('The "component" property in the SelectorHookParserConfig must either contain the component class or a LazyLoadComponentConfig.');
      }
      // name
      if (userParserConfig.hasOwnProperty('name')) {
        if (typeof userParserConfig.name !== 'string') {
          throw Error('The submitted "name" property in the SelectorHookParserConfig must be of type string, was ' + typeof userParserConfig.name);
        }
        parserConfig.name = userParserConfig.name;
      }
      // selector (defaults to component selector)
      if (userParserConfig.hasOwnProperty('selector')) {
        if (typeof userParserConfig.selector !== 'string') {
          throw Error('The submitted "selector" property in the SelectorHookParserConfig must be of type string, was ' + typeof userParserConfig.selector);
        }
        parserConfig.selector = userParserConfig.selector;
      }
      // injector (defaults to undefined)
      if (userParserConfig.hasOwnProperty('injector')) {
        parserConfig.injector = userParserConfig.injector;
      }
      // enclosing
      if (userParserConfig.hasOwnProperty('enclosing')) {
        if (typeof userParserConfig.enclosing !== 'boolean') {
          throw Error('The submitted "enclosing" property in the SelectorHookParserConfig must be of type boolean, was ' + typeof userParserConfig.enclosing);
        }
        parserConfig.enclosing = userParserConfig.enclosing;
      }
      // bracketStyle
      if (userParserConfig.hasOwnProperty('bracketStyle')) {
        if (typeof userParserConfig.bracketStyle !== 'object' || typeof userParserConfig.bracketStyle.opening !== 'string' || typeof userParserConfig.bracketStyle.closing !== 'string') {
          throw Error('The submitted "bracketStyle" property in the SelectorHookParserConfig must have the form {opening: string, closing: string}');
        }
        parserConfig.bracketStyle = userParserConfig.bracketStyle;
      }
      // unescapeStrings
      if (userParserConfig.hasOwnProperty('unescapeStrings')) {
        if (typeof userParserConfig.unescapeStrings !== 'boolean') {
          throw Error('The submitted "unescapeStrings" property in the SelectorHookParserConfig must be of type boolean, was ' + typeof userParserConfig.unescapeStrings);
        }
        parserConfig.unescapeStrings = userParserConfig.unescapeStrings;
      }
      // parseInputs
      if (userParserConfig.hasOwnProperty('parseInputs')) {
        if (typeof userParserConfig.parseInputs !== 'boolean') {
          throw Error('The submitted "parseInputs" property in the SelectorHookParserConfig must be of type boolean, was ' + typeof userParserConfig.parseInputs);
        }
        parserConfig.parseInputs = userParserConfig.parseInputs;
      }
      // inputsBlacklist
      if (userParserConfig.hasOwnProperty('inputsBlacklist')) {
        if (!Array.isArray(userParserConfig.inputsBlacklist)) {
          throw Error('The submitted "inputsBlacklist" property in the SelectorHookParserConfig must be an array of strings.');
        }
        for (const entry of userParserConfig.inputsBlacklist) {
          if (typeof entry !== 'string') {
            throw Error('All entries of the submitted "inputsBlacklist" property in the SelectorHookParserConfig must be of type string, ' + typeof entry + ' found.');
          }
        }
        parserConfig.inputsBlacklist = userParserConfig.inputsBlacklist;
      }
      // inputsWhitelist
      if (userParserConfig.hasOwnProperty('inputsWhitelist')) {
        if (!Array.isArray(userParserConfig.inputsWhitelist)) {
          throw Error('The submitted "inputsWhitelist" property in the SelectorHookParserConfig must be an array of strings.');
        }
        for (const entry of userParserConfig.inputsWhitelist) {
          if (typeof entry !== 'string') {
            throw Error('All entries of the submitted "inputsWhitelist" property in the SelectorHookParserConfig must be of type string, ' + typeof entry + ' found.');
          }
        }
        parserConfig.inputsWhitelist = userParserConfig.inputsWhitelist;
      }
      // outputsBlacklist
      if (userParserConfig.hasOwnProperty('outputsBlacklist')) {
        if (!Array.isArray(userParserConfig.outputsBlacklist)) {
          throw Error('The submitted "outputsBlacklist" property in the SelectorHookParserConfig must be an array of strings.');
        }
        for (const entry of userParserConfig.outputsBlacklist) {
          if (typeof entry !== 'string') {
            throw Error('All entries of the submitted "outputsBlacklist" property in the SelectorHookParserConfig must be of type string, ' + typeof entry + ' found.');
          }
        }
        parserConfig.outputsBlacklist = userParserConfig.outputsBlacklist;
      }
      // outputsWhitelist
      if (userParserConfig.hasOwnProperty('outputsWhitelist')) {
        if (!Array.isArray(userParserConfig.outputsWhitelist)) {
          throw Error('The submitted "outputsWhitelist" property in the SelectorHookParserConfig must be an array of strings.');
        }
        for (const entry of userParserConfig.outputsWhitelist) {
          if (typeof entry !== 'string') {
            throw Error('All entries of the submitted "outputsWhitelist" property in the SelectorHookParserConfig must be of type string, ' + typeof entry + ' found.');
          }
        }
        parserConfig.outputsWhitelist = userParserConfig.outputsWhitelist;
      }
      // allowContextInBindings
      if (userParserConfig.hasOwnProperty('allowContextInBindings')) {
        if (typeof userParserConfig.allowContextInBindings !== 'boolean') {
          throw Error('The submitted "allowContextInBindings" property in the SelectorHookParserConfig must be of type boolean, was ' + typeof userParserConfig.allowContextInBindings);
        }
        parserConfig.allowContextInBindings = userParserConfig.allowContextInBindings;
      }
      // allowContextFunctionCalls
      if (userParserConfig.hasOwnProperty('allowContextFunctionCalls')) {
        if (typeof userParserConfig.allowContextFunctionCalls !== 'boolean') {
          throw Error('The submitted "allowContextFunctionCalls" property in the SelectorHookParserConfig must be of type boolean, was ' + typeof userParserConfig.allowContextFunctionCalls);
        }
        parserConfig.allowContextFunctionCalls = userParserConfig.allowContextFunctionCalls;
      }
      return parserConfig;
    }
  }
  SelectorHookParserConfigResolver.ɵfac = function SelectorHookParserConfigResolver_Factory(t) {
    return new (t || SelectorHookParserConfigResolver)(i0.ɵɵinject(i0.ComponentFactoryResolver));
  };
  SelectorHookParserConfigResolver.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: SelectorHookParserConfigResolver,
    factory: SelectorHookParserConfigResolver.ɵfac
  });
  return SelectorHookParserConfigResolver;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const regexes = {};
// General
const variableName = '[a-zA-Z_$]+[a-zA-Z0-9_$]*';
const attributeName = '[a-zA-Z_$\\-]+[a-zA-Z0-9_$]*';
// Attribute regex
regexes.attributeNameNoBracketsRegex = '(' + attributeName + ')';
regexes.attributeNameBracketsRegex = '\\[(' + attributeName + ')\\]';
regexes.attributeNameRoundBracketsRegex = '\\((' + attributeName + ')\\)';
regexes.attributeNameRegex = '(?:' + regexes.attributeNameNoBracketsRegex + '|' + regexes.attributeNameBracketsRegex + '|' + regexes.attributeNameRoundBracketsRegex + ')';
regexes.attributeValueDoubleQuotesRegex = '\"((?:\\\\.|[^\"])*?)\"'; // Clever bit of regex to allow escaped chars in strings: https://stackoverflow.com/a/1016356/3099523
regexes.attributeValueSingleQuotesRegex = '\'((?:\\\\.|[^\'])*?)\'';
// Context var regex examples: https://regex101.com/r/zSbY7M/4
// Supports the dot notation, the [] notation as well as function calls () for building variable paths
regexes.variablePathDotNotation = '\\.' + variableName;
regexes.variableBracketsNotation = '\\[[^\\]]*\\]'; // Relies on nested '[]'brackets being encoded
regexes.variablePathFunctionCall = '\\([^\\)]*\\)'; // Relies on nested '()'-brackets being encoded.
regexes.variablePathPartRegex = '(?:' + regexes.variablePathDotNotation + '|' + regexes.variableBracketsNotation + '|' + regexes.variablePathFunctionCall + ')';
regexes.contextVariableRegex = 'context' + regexes.variablePathPartRegex + '*';
regexes.placeholderVariablePathDotNotation = '\\@@@cxtDot@@@' + variableName;
regexes.placeholderVariableBracketsNotation = '@@@cxtOpenSquareBracket@@@[^\\]]*@@@cxtCloseSquareBracket@@@';
regexes.placeholderVariablePathFunctionCall = '@@@cxtOpenRoundBracket@@@[^\\)]*@@@cxtCloseRoundBracket@@@';
regexes.placeholderVariablePathPartRegex = '(?:' + regexes.placeholderVariablePathDotNotation + '|' + regexes.placeholderVariableBracketsNotation + '|' + regexes.placeholderVariablePathFunctionCall + ')';
regexes.placeholderContextVariableRegex = '__CXT__' + regexes.placeholderVariablePathPartRegex + '*';

/**
 * Polyfill for String.prototype.matchAll() from the ES2020 spec
 *
 * Note: The 'string.prototype.matchall' npm package was unstable for me so providing my own version here
 *
 * @param text - The text to search
 * @param regExp - The RegExp object to use
 */
function matchAll(text, regExp) {
  // Must be global
  if (!regExp.global) {
    throw Error('TypeError: matchAll called with a non-global RegExp argument');
  }
  // Get matches
  const result = [];
  let match = regExp.exec(text);
  while (match !== null) {
    result.push(match);
    match = regExp.exec(text);
  }
  // Reset internal index
  regExp.lastIndex = 0;
  return result;
}

/**
 * A utility service to easily parse hooks from a string of text
 */
let HookFinder = /*#__PURE__*/(() => {
  class HookFinder {
    /**
     * Finds all hooks that are non-enclosing in a string of text, e.g. <hook>
     *
     * @param content - The text to search
     * @param hookRegex - The regex to use for the hook
     */
    findStandaloneHooks(content, hookRegex) {
      const result = [];
      // Find all hooks
      const openingTagMatches = matchAll(content, hookRegex);
      for (const match of openingTagMatches) {
        result.push({
          openingTagStartIndex: match.index,
          openingTagEndIndex: match.index + match[0].length,
          closingTagStartIndex: null,
          closingTagEndIndex: null
        });
      }
      return result;
    }
    /**
     * Finds all hooks that are enclosing in a string of text, e.g. <hook>...</hook>
     *
     * Correctly finding enclosing hooks requires a programmatic parser rather then just regex alone, as regex cannot handle
     * patterns that are potentially nested within themselves.
     *
     * - If the content between the opening and closing is lazy (.*?), it would take the first closing tag after the opening tag,
     *   regardless if it belongs to the opening tag or actually a nested hook. This would falsely match the first and third tag
     *   in this example: '<hook><hook></hook></hook>'
     *
     * - If the content between the opening and closing is greedy (.*), it would only end on the last closing tag in the string,
     *   ignoring any previous closing tags. This would falsely match the first and fourth tag in this example:
     *   '<hook></hook><hook></hook>'
     *
     * There is no regex that works for both scenarios. This method therefore manually counts and compares the opening tags with the closing tags.
     *
     * @param content - The text to parse
     * @param openingTagRegex - The regex for the opening tag
     * @param closingTagRegex - The regex for the closing tag
     * @param includeNested - Whether to include nested hooks in the result
     */
    findEnclosingHooks(content, openingTagRegex, closingTagRegex, includeNested = true) {
      const allTags = [];
      const result = [];
      // Find all opening tags
      const openingTagMatches = matchAll(content, openingTagRegex);
      for (const match of openingTagMatches) {
        allTags.push({
          isOpening: true,
          value: match[0],
          startIndex: match.index,
          endIndex: match.index + match[0].length
        });
      }
      // Find all closing tags
      const closingTagMatches = matchAll(content, closingTagRegex);
      for (const match of closingTagMatches) {
        allTags.push({
          isOpening: false,
          value: match[0],
          startIndex: match.index,
          endIndex: match.index + match[0].length
        });
      }
      // Sort by startIndex
      allTags.sort((a, b) => a.startIndex - b.startIndex);
      // Create HookPositions by figuring out which opening tag belongs to which closing tag
      const openedTags = [];
      allTagsLoop: for (const [index, tag] of allTags.entries()) {
        // Any subsequent tag is only allowed to start after previous tag has ended
        if (index > 0 && tag.startIndex < allTags[index - 1].endIndex) {
          if (isDevMode()) {
            console.warn('Syntax error - New tag "' + tag.value + '" started at position ' + tag.startIndex + ' before previous tag "' + allTags[index - 1].value + '" ended at position ' + allTags[index - 1].endIndex + '. Ignoring.');
          }
          continue;
        }
        // Opening or closing tag?
        if (tag.isOpening) {
          openedTags.push(tag);
        } else {
          // Syntax error: Closing tag without preceding opening tag. Syntax error.
          if (openedTags.length === 0) {
            if (isDevMode()) {
              console.warn('Syntax error - Closing tag without preceding opening tag found: "' + tag.value + '". Ignoring.');
            }
            continue;
          }
          // If nested hooks not allowed and more than one tag is open, discard both this closing tag and the latest opening tag
          if (!includeNested && openedTags.length > 1) {
            openedTags.pop();
            continue;
          }
          // Valid hook! Add to result array
          const openingTag = openedTags[openedTags.length - 1];
          result.push({
            openingTagStartIndex: openingTag.startIndex,
            openingTagEndIndex: openingTag.startIndex + openingTag.value.length,
            closingTagStartIndex: tag.startIndex,
            closingTagEndIndex: tag.startIndex + tag.value.length
          });
          openedTags.pop();
        }
      }
      if (openedTags.length > 0) {
        if (isDevMode()) {
          console.warn('Syntax error - Opening tags without corresponding closing tags found.');
        }
      }
      return result;
    }
  }
  HookFinder.ɵfac = function HookFinder_Factory(t) {
    return new (t || HookFinder)();
  };
  HookFinder.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: HookFinder,
    factory: HookFinder.ɵfac
  });
  return HookFinder;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * A service for SelectorHookParser, responsible for finding Angular component selectors in the content
 */
let SelectorHookFinder = /*#__PURE__*/(() => {
  class SelectorHookFinder {
    constructor(hookFinder) {
      this.hookFinder = hookFinder;
    }
    /**
     * Finds standalone Angular component selectors
     *
     * @param content - The content to parse
     * @param selector - The Angular selector to find
     * @param bracketStyle - What bracket style to use
     */
    findStandaloneSelectors(content, selector, bracketStyle = {
      opening: '<',
      closing: '>'
    }) {
      // Create opening tag regex
      const openingTagRegex = this.generateOpeningTagRegex(selector, bracketStyle);
      return this.hookFinder.findStandaloneHooks(content, openingTagRegex);
    }
    /**
     * Finds enclosing Angular component selectors
     *
     * @param content - The content to parse
     * @param selector - The Angular selector to find
     * @param bracketStyle - What bracket style to use
     */
    findEnclosingSelectors(content, selector, bracketStyle = {
      opening: '<',
      closing: '>'
    }) {
      // Create opening and closing tag regex
      const openingTagRegex = this.generateOpeningTagRegex(selector, bracketStyle);
      const closingTagRegex = this.generateClosingTagRegex(selector, bracketStyle);
      return this.hookFinder.findEnclosingHooks(content, openingTagRegex, closingTagRegex, true);
    }
    // Hook regex helper
    // ----------------------------------------------------------------------------------------------------------------------------------------
    /**
     * Generates the opening tag regex for a standard Angular component selector
     *
     * @param selector - The selector name
     * @param bracketStyle - What bracket style to use
     */
    generateOpeningTagRegex(selector, bracketStyle = {
      opening: '<',
      closing: '>'
    }) {
      // Find opening tag of hook lazily
      // Examples for this regex: https://regex101.com/r/WjTsmA/1
      // Features: Ignores redundant whitespace & line-breaks, supports n attributes, both normal and []-attribute-name-syntax, both ' and " as attribute-value delimiters
      const openingArrow = this.escapeRegex(bracketStyle.opening);
      const selectorName = this.escapeRegex(selector);
      const closingArrow = this.escapeRegex(bracketStyle.closing);
      const space = '\\s';
      const attributeValuesOR = '(?:' + regexes.attributeValueDoubleQuotesRegex + '|' + regexes.attributeValueSingleQuotesRegex + ')';
      const attributes = '(?:' + space + '+' + regexes.attributeNameRegex + '\=' + attributeValuesOR + ')+';
      const fullRegex = openingArrow + selectorName + '(?:' + space + '*' + closingArrow + '|' + attributes + space + '*' + closingArrow + ')';
      const regexObject = new RegExp(fullRegex, 'gim');
      return regexObject;
    }
    /**
     * Generates the opening tag regex for a standard hook
     *
     * @param selector - The selector of the hook
     * @param bracketStyle - What bracket style to use
     */
    generateClosingTagRegex(selector, bracketStyle = {
      opening: '<',
      closing: '>'
    }) {
      const openingArrow = this.escapeRegex(bracketStyle.opening) + '\/';
      const selectorName = this.escapeRegex(selector);
      const closingArrow = this.escapeRegex(bracketStyle.closing);
      const fullRegex = openingArrow + selectorName + closingArrow;
      const regexObject = new RegExp(fullRegex, 'gim');
      return regexObject;
    }
    /**
     * Safely escapes a string for use in regex
     *
     * @param text - The string to escape
     */
    escapeRegex(text) {
      return text.replace(new RegExp('[-\\/\\\\^$*+?.()|[\\]{}]', 'g'), '\\$&');
    }
  }
  SelectorHookFinder.ɵfac = function SelectorHookFinder_Factory(t) {
    return new (t || SelectorHookFinder)(i0.ɵɵinject(HookFinder));
  };
  SelectorHookFinder.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: SelectorHookFinder,
    factory: SelectorHookFinder.ɵfac
  });
  return SelectorHookFinder;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * A service that provides various functions for en- and decoding data type strings in order to make them
 * meaningfully parseable by regex
 */
let DataTypeEncoder = /*#__PURE__*/(() => {
  class DataTypeEncoder {
    constructor() {}
    // Substrings
    // -----------------------------------------------------
    /**
     * Finds all substrings in a piece of text and replaces all special characters within with @@@...@@@-placeholders.
     *
     * @param text - The text to parse for substrings
     */
    encodeSubstrings(text) {
      // Get a list of all quotes (that are not preceded by an escaping backslash)
      const singleQuotes = matchAll(text, /'/gm).filter(match => match.index === 0 || text[match.index - 1] !== '\\');
      const doubleQuotes = matchAll(text, /"/gm).filter(match => match.index === 0 || text[match.index - 1] !== '\\');
      const graveQuotes = matchAll(text, /`/gm).filter(match => match.index === 0 || text[match.index - 1] !== '\\');
      const allQuotes = [...singleQuotes, ...doubleQuotes, ...graveQuotes];
      allQuotes.sort((a, b) => a['index'] - b['index']);
      // Create quotes text segments
      const quoteSegments = [];
      let outermostOpenedQuote = null;
      for (const quote of allQuotes) {
        if (!outermostOpenedQuote) {
          outermostOpenedQuote = quote;
        } else {
          if (outermostOpenedQuote[0] === quote[0]) {
            quoteSegments.push({
              startIndex: outermostOpenedQuote.index + 1,
              endIndex: quote['index']
            });
            outermostOpenedQuote = null;
          }
        }
      }
      if (outermostOpenedQuote !== null) {
        throw Error('Input parse error. String was opened, but not closed.');
      }
      // Encode quote segments
      const encodedBracketsText = this.encodeTextSegments(text, quoteSegments, this.encodeStringSpecialChars);
      return encodedBracketsText;
    }
    /**
     * Encodes all special characters that might be confused as code syntax in a piece of string
     *
     * @param text - The text to encode
     */
    encodeStringSpecialChars(text) {
      text = text.replace(/'/g, '@@@singlequote@@@');
      text = text.replace(/"/g, '@@@doublequote@@@');
      text = text.replace(/`/g, '@@@gravequote@@@');
      text = text.replace(/:/g, '@@@colon@@@');
      text = text.replace(/;/g, '@@@semicolon@@@');
      text = text.replace(/\./g, '@@@dot@@@');
      text = text.replace(/,/g, '@@@comma@@@');
      text = text.replace(/\\/g, '@@@backslash@@@');
      text = text.replace(/\(/g, '@@@openRoundBracket@@@');
      text = text.replace(/\)/g, '@@@closeRoundBracket@@@');
      text = text.replace(/\[/g, '@@@openSquareBracket@@@');
      text = text.replace(/\]/g, '@@@closeSquareBracket@@@');
      text = text.replace(/\{/g, '@@@openCurlyBracket@@@');
      text = text.replace(/\}/g, '@@@closeCurlyBracket@@@');
      return text;
    }
    /**
     * Decodes the special characters again
     *
     * @param text - The text to decode
     */
    decodeStringSpecialChars(text) {
      text = text.replace(/@@@singlequote@@@/g, '\'');
      text = text.replace(/@@@doublequote@@@/g, '"');
      text = text.replace(/@@@gravequote@@@/g, '`');
      text = text.replace(/@@@colon@@@/g, ':');
      text = text.replace(/@@@semicolon@@@/g, ';');
      text = text.replace(/@@@dot@@@/g, '.');
      text = text.replace(/@@@comma@@@/g, ',');
      text = text.replace(/@@@backslash@@@/g, '\\');
      text = text.replace(/@@@openRoundBracket@@@/g, '(');
      text = text.replace(/@@@closeRoundBracket@@@/g, ')');
      text = text.replace(/@@@openSquareBracket@@@/g, '[');
      text = text.replace(/@@@closeSquareBracket@@@/g, ']');
      text = text.replace(/@@@openCurlyBracket@@@/g, '{');
      text = text.replace(/@@@closeCurlyBracket@@@/g, '}');
      return text;
    }
    // Subfunctions
    // -----------------------------------------------------
    /**
     * Finds all subfunctions in a piece of text and replaces their round brackets with @@@...@@@-placeholders.
     *
     * @param text - The text to parse for substrings
     */
    encodeSubfunctions(text) {
      const openingBrackets = matchAll(text, /\(/gm);
      const closingBrackets = matchAll(text, /\)/gm);
      const allBrackets = [...openingBrackets, ...closingBrackets];
      allBrackets.sort((a, b) => a['index'] - b['index']);
      // Create functions text segments
      const functionSegments = [];
      const openedBrackets = [];
      for (const bracket of allBrackets) {
        if (bracket[0] === '(') {
          openedBrackets.push(bracket);
        } else {
          if (openedBrackets.length === 0) {
            throw Error('Input parse error. Closed function bracket without opening it first.');
          }
          // Only collect the outermost function brackets, not nested ones
          if (openedBrackets.length === 1) {
            functionSegments.push({
              startIndex: openedBrackets[0].index + 1,
              endIndex: bracket['index']
            });
          }
          openedBrackets.pop();
        }
      }
      if (openedBrackets.length !== 0) {
        throw Error('Input parse error. Opened function bracket without closing it.');
      }
      // Encode quote segments
      const encodedFunctionsText = this.encodeTextSegments(text, functionSegments, this.encodeFunctionBrackets);
      return encodedFunctionsText;
    }
    /**
     * Encodes all round brackets with harmless placeholders
     *
     * @param text - The text to encode
     */
    encodeFunctionBrackets(text) {
      text = text.replace(/\(/g, '@@@fnOpenBracket@@@');
      text = text.replace(/\)/g, '@@@fnCloseBracket@@@');
      return text;
    }
    /**
     * Decodes all round brackets again
     *
     * @param text - The text to decode
     */
    decodeFunctionBrackets(text) {
      text = text.replace(/@@@fnOpenBracket@@@/g, '\(');
      text = text.replace(/@@@fnCloseBracket@@@/g, '\)');
      return text;
    }
    // Subbrackets
    // -----------------------------------------------------
    /**
     * Finds all subbrackets in a piece of text and replaces their brackets with @@@...@@@-placeholders.
     *
     * @param text - The text to parse for substrings
     */
    encodeVariableSubbrackets(text) {
      // Property accessor opening brackets can be identified by what they are preceded by.
      // Must be a) text, b) closing square bracket or c) closing round bracket. Arrays can't be preceded by any of these.
      const variableOpeningBracketsWithLookbehinds = '(?<=[a-zA-Z_$\\]\)])\\['; // Too new for many older browsers
      const variableOpeningBrackets = '(?:[a-zA-Z_$\\]\)])(\\[)';
      const openingBrackets = matchAll(text, new RegExp(variableOpeningBrackets, 'gm'));
      // Note: Can't simply find closing brackets as well (as is done in the other encoder functions), because the closing
      // bracket doesn't have a uniquely identifiable syntax. Might also be array endings.
      // Find the corresponding closing bracket for each opening bracket by parsing the following brackets
      const bracketSegments = [];
      for (const openingBracket of openingBrackets) {
        const followingText = text.substr(openingBracket.index + 2); // openingBracket.index + 2, b/c the regex starts at the character before the opening bracket and followingText is supposed to start after the opening bracket
        const followingOpeningBrackets = matchAll(followingText, /\[/gm);
        const followingClosingBrackets = matchAll(followingText, /\]/gm);
        const allFollowingBrackets = [...followingOpeningBrackets, ...followingClosingBrackets];
        allFollowingBrackets.sort((a, b) => a['index'] - b['index']);
        let openedBrackets = 1; // Start with the first opening bracket already counted
        for (const followingBracket of allFollowingBrackets) {
          openedBrackets = followingBracket[0] === ']' ? openedBrackets - 1 : openedBrackets + 1;
          if (openedBrackets === 0) {
            bracketSegments.push({
              startIndex: openingBracket.index + 2,
              endIndex: openingBracket.index + 2 + followingBracket['index']
            });
            break;
          }
        }
        if (openedBrackets !== 0) {
          throw Error('Input parse error. Opened bracket without closing it.');
        }
      }
      // Throw out nested brackets
      const outerBracketSegments = [];
      for (const bracketSegment of bracketSegments) {
        if (outerBracketSegments.length === 0) {
          outerBracketSegments.push(bracketSegment);
        } else {
          if (outerBracketSegments[outerBracketSegments.length - 1].endIndex < bracketSegment.startIndex) {
            outerBracketSegments.push(bracketSegment);
          }
        }
      }
      // Encode bracket segments
      const encodedBracketsText = this.encodeTextSegments(text, outerBracketSegments, this.encodeVariableBrackets);
      return encodedBracketsText;
    }
    /**
     * Encodes all brackets with harmless placeholders
     *
     * @param text - The text to encode
     */
    encodeVariableBrackets(text) {
      text = text.replace(/\[/g, '@@@variableOpeningBracket@@@');
      text = text.replace(/\]/g, '@@@variableClosingBracket@@@');
      return text;
    }
    /**
     * Decodes all brackets again
     *
     * @param text - The text to encode
     */
    decodeVariableBrackets(text) {
      text = text.replace(/@@@variableOpeningBracket@@@/g, '\[');
      text = text.replace(/@@@variableClosingBracket@@@/g, '\]');
      return text;
    }
    // Context var placeholder
    // -----------------------------------------------------
    /**
     * Transforms a context var (that is already encoded for substrings, subfunctions and subbrackets) into a string placeholder
     * by encoding the context var syntax itself. This is so that can be safely parsed by JSON.parse() as a string and also so it
     * won't be misinterpreted by other regexes looking for JSON syntax (especially arrays b/c of context-var []-property-brackets)
     *
     * @param contextVar - The context var to transform
     */
    transformContextVarIntoPlacerholder(contextVar) {
      // Replace context. with __CXT__
      contextVar = '__CXT__' + contextVar.substr(7);
      // Encode variable syntax
      contextVar = contextVar.replace(/\"/g, '@@@cxtDoubleQuote@@@');
      contextVar = contextVar.replace(/\./g, '@@@cxtDot@@@');
      contextVar = contextVar.replace(/\[/g, '@@@cxtOpenSquareBracket@@@');
      contextVar = contextVar.replace(/\]/g, '@@@cxtCloseSquareBracket@@@');
      contextVar = contextVar.replace(/\(/g, '@@@cxtOpenRoundBracket@@@');
      contextVar = contextVar.replace(/\)/g, '@@@cxtCloseRoundBracket@@@');
      return contextVar;
    }
    transformPlaceholderIntoContextVar(contextVar) {
      contextVar = 'context' + contextVar.substr(7);
      contextVar = contextVar.replace(/@@@cxtDoubleQuote@@@/g, '"');
      contextVar = contextVar.replace(/@@@cxtDot@@@/g, '.');
      contextVar = contextVar.replace(/@@@cxtOpenSquareBracket@@@/g, '[');
      contextVar = contextVar.replace(/@@@cxtCloseSquareBracket@@@/g, ']');
      contextVar = contextVar.replace(/@@@cxtOpenRoundBracket@@@/g, '(');
      contextVar = contextVar.replace(/@@@cxtCloseRoundBracket@@@/g, ')');
      return contextVar;
    }
    // Other
    // -----------------------------------------------------
    /**
     * Takes a piece of text as well as array of TextSegments and encodes them with the help of an encodingFunction
     * The encoded text is then automatically assembled and returned.
     *
     * @param text - The text in question
     * @param specialTextSegments - The segments in the text to encode
     * @param encodingFunction - The encoding function to use
     */
    encodeTextSegments(text, specialTextSegments, encodingFunction) {
      // 1. Divide whole text into two types of segments: Those to be encoded and those to be left as they are
      const allTextSegments = [];
      for (const specialTextSegment of specialTextSegments) {
        // Push normal text segment since last special segment
        const lastSegmentEndIndex = allTextSegments.length === 0 ? 0 : allTextSegments[allTextSegments.length - 1].endIndex;
        allTextSegments.push({
          type: 'text',
          startIndex: lastSegmentEndIndex,
          endIndex: specialTextSegment.startIndex,
          string: text.substr(lastSegmentEndIndex, specialTextSegment.startIndex - lastSegmentEndIndex)
        });
        // Push next special segment
        allTextSegments.push({
          type: 'special',
          startIndex: specialTextSegment.startIndex,
          endIndex: specialTextSegment.endIndex,
          string: text.substr(specialTextSegment.startIndex, specialTextSegment.endIndex - specialTextSegment.startIndex)
        });
      }
      // Add text segment for trailing text after last special segment
      const lastBracketEndIndex = allTextSegments.length === 0 ? 0 : allTextSegments[allTextSegments.length - 1].endIndex;
      allTextSegments.push({
        type: 'text',
        startIndex: lastBracketEndIndex,
        endIndex: text.length - 1,
        string: text.substr(lastBracketEndIndex)
      });
      // 2. Encode all special segments
      for (const segment of allTextSegments) {
        if (segment.type === 'special') {
          segment.string = encodingFunction(segment.string);
        }
      }
      // 3. Concat everything together again
      let encodedString = '';
      for (const segment of allTextSegments) {
        encodedString += segment.string;
      }
      return encodedString;
    }
    /**
     * Strips all escaping backslashes from a piece of text
     *
     * @param text - The text in question
     */
    stripSlashes(text) {
      return text.replace(/\\(.)/g, '$1');
      // return text.replace(new RegExp('\\\\(.)', 'g'), '$1');
    }
    /**
     * Escapes all double quotes in a piece of text
     *
     * @param text - The text in question
     */
    escapeDoubleQuotes(text) {
      const result = text.replace(/\\/g, '\\\\').replace(/\"/g, '\\"');
      return result;
    }
  }
  DataTypeEncoder.ɵfac = function DataTypeEncoder_Factory(t) {
    return new (t || DataTypeEncoder)();
  };
  DataTypeEncoder.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: DataTypeEncoder,
    factory: DataTypeEncoder.ɵfac
  });
  return DataTypeEncoder;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * A parser that can evaluate Javascript data types from strings and turn them into live variables
 */
let DataTypeParser = /*#__PURE__*/(() => {
  class DataTypeParser {
    constructor(dataTypeEncoder) {
      this.dataTypeEncoder = dataTypeEncoder;
    }
    /**
     * Takes a string containing a Javascript data type as it would in code, such a number ('15'), a string ('"hello"'),
     * an array ('[1,2,3]'), an object ('{prop: "something"}') etc., and evaluates it to be an an actual variable.
     *
     * Note: This function works without invoking eval() and instead uses JSON.parse() for the heavy lifting. As such, it should be safe
     * to use and should cover most forms of input.
     *
     * @param dataTypeString - The string to parse
     * @param context - (optional) A context object to load variables from
     * @param event - (optional) An event object to place $event vars with
     * @param unescapeStrings - (optional) Whether to unescape strings or not
     * @param trackContextVariables - (optional) An object that will be filled out with all found context vars
     * @param allowContextFunctionCalls - (optional) Whether to allow function calls in context vars
     */
    evaluate(dataTypeString, context = {}, event, unescapeStrings = true, trackContextVariables = {}, allowContextFunctionCalls = true) {
      // a) Simple types
      // --------------------
      // null or undefined
      if (dataTypeString === 'null') {
        return null;
      }
      if (dataTypeString === 'undefined') {
        return undefined;
      }
      // boolean
      if (dataTypeString === 'true') {
        return true;
      }
      if (dataTypeString === 'false') {
        return false;
      }
      // number
      if (!isNaN(dataTypeString)) {
        return parseInt(dataTypeString, 10);
      }
      // string
      if (dataTypeString.startsWith('"') && dataTypeString.endsWith('"') || dataTypeString.startsWith("'") && dataTypeString.endsWith("'") || dataTypeString.startsWith("`") && dataTypeString.endsWith("`")) {
        // Remove outer quotes, potentially unescape and return
        let decodedString = dataTypeString.substr(1, dataTypeString.length - 2);
        decodedString = unescapeStrings ? this.dataTypeEncoder.stripSlashes(decodedString) : decodedString;
        return decodedString;
      }
      // b) Complex types
      // --------------------
      // IMPORTANT: To properly parse complex object structures as well as context variables with regex, the string needs to be prepared. This means:
      // 1. Substrings must be rendered 'harmless', meaning all special characters that regex might confuse with variable syntax must be encoded.
      // 2. The brackets of subfunctions (e.g. context.fn(otherFn(param)).var), must be encoded as well. Regex can't handle nested substructures and wouldn't know which bracket closes the outer function.
      // 3. The brackets of subbrackets (e.g. context[context['something']].var) must be encoded for the same reason.
      dataTypeString = this.encodeDataTypeString(dataTypeString);
      // array or object literal
      if (dataTypeString.startsWith('{') && dataTypeString.endsWith('}') || dataTypeString.startsWith('[') && dataTypeString.endsWith(']')) {
        // Prepare string and parse as JSON
        const json = this.parseAsJSON(dataTypeString, unescapeStrings);
        // Load variables
        return this.loadVariables(json, context, event, unescapeStrings, trackContextVariables, allowContextFunctionCalls);
      }
      // event variable name
      if (dataTypeString === '$event') {
        return event;
      }
      // context variable name
      if (dataTypeString.match(new RegExp('^\\s*' + regexes.contextVariableRegex + '\\s*$', 'gm'))) {
        return this.safelyLoadContextVariable(dataTypeString, context, event, unescapeStrings, trackContextVariables, allowContextFunctionCalls);
      }
      throw Error('Data type for following input was not recognized and could not be parsed: "' + dataTypeString + '"');
    }
    /**
     * Encodes a data type string
     *
     * @param dataTypeString - The string to encode
     */
    encodeDataTypeString(dataTypeString) {
      dataTypeString = this.dataTypeEncoder.encodeSubstrings(dataTypeString); // Encode all potential substrings
      dataTypeString = this.dataTypeEncoder.encodeSubfunctions(dataTypeString); // Encode all potential subfunctions
      dataTypeString = this.dataTypeEncoder.encodeVariableSubbrackets(dataTypeString); // Encode all potential subbrackets of variables
      return dataTypeString;
    }
    /**
     * Decodes a data type string
     *
     * @param dataTypeString - The string to decode
     */
    decodeDataTypeString(dataTypeString) {
      dataTypeString = this.dataTypeEncoder.decodeStringSpecialChars(dataTypeString); // Decode special chars from substrings
      dataTypeString = this.dataTypeEncoder.decodeFunctionBrackets(dataTypeString); // Decode subfunctions
      dataTypeString = this.dataTypeEncoder.decodeVariableBrackets(dataTypeString); // Decode subbrackets
      dataTypeString = dataTypeString.trim(); // Trim whitespace
      return dataTypeString;
    }
    /**
     * In order to successfully parse a data type string with JSON.parse(), it needs to follow certain formatting rules.
     * This functions ensures that these are followed and corrects the input if not.
     *
     * @param JSONString - The string to be given to JSON.parse()
     * @param unescapeStrings - Whether to unescape the strings of this JSON
     */
    parseAsJSON(JSONString, unescapeStrings = true) {
      // Find all single- and grave-quote-delimited strings and convert them to double quote strings
      const singleQuoteStringRegex = /\'(\\.|[^\'])*?\'/gm;
      JSONString = JSONString.replace(singleQuoteStringRegex, match => {
        return '"' + match.slice(1, -1) + '"';
      });
      const graveQuoteStringRegex = /\`(\\.|[^\`])*?\`/gm;
      JSONString = JSONString.replace(graveQuoteStringRegex, match => {
        return '"' + match.slice(1, -1) + '"';
      });
      // Add double-quotes around JSON property names where still missing
      const JSONPropertyRegex = /"?([a-z0-9A-Z_]+)"?\s*:/g;
      JSONString = JSONString.replace(JSONPropertyRegex, '"$1": ');
      // Prevent setting protected properties
      if (JSONString.match(/"?__proto__"?\s*:/g)) {
        throw Error('Setting the "__proto__" property in a hook input object is not allowed.');
      }
      if (JSONString.match(/"?prototype"?\s*:/g)) {
        throw Error('Setting the "prototype" property in a hook input object is not allowed.');
      }
      if (JSONString.match(/"?constructor"?\s*:/g)) {
        throw Error('Setting the "constructor" property in a hook input object is not allowed.');
      }
      // Replace undefined with null
      JSONString = this.replaceValuesInJSONString(JSONString, 'undefined', match => 'null');
      // Replace context vars with string placeholders
      JSONString = this.replaceValuesInJSONString(JSONString, regexes.contextVariableRegex, match => {
        return '"' + this.dataTypeEncoder.transformContextVarIntoPlacerholder(match) + '"';
      });
      // Replace $event with string placeholders
      JSONString = this.replaceValuesInJSONString(JSONString, '\\$event', match => '"__EVENT__"');
      // PARSE
      const json = JSON.parse(JSONString);
      // Decode all strings that are not context vars or the event object
      this.decodeJSONStrings(json, unescapeStrings);
      return json;
    }
    /**
     * Given a stringified json and a json value regex, allows you to replace all occurences
     * of those values in the json via a callback function.
     *
     * IMPORTANT: JSONString must be already encoded via this.encodeDataTypeString() for this to work.
     *
     * @param JSONString - The stringified JSON
     * @param valueRegex - The values to find
     * @param callbackFn - A callback fn that returns what you want to replace them with
     */
    replaceValuesInJSONString(JSONString, valueRegex, callbackFn) {
      // With lookbehinds (too new for some browsers)
      const withLookBehindsRegex = '(?:' + '(?<=:\\s*)' + valueRegex + '(?=\\s*[,}])' + '|' + '(?<=[\\[,]\\s*)' + valueRegex + '(?=\\s*[\\],])' + ')';
      // Without lookbehinds (make sure to keep the lookaheads, though. This way, the same comma can be the end of one regex and the beginning of the next)
      const regex = '(?:' + '(:\\s*)(' + valueRegex + ')(?=\\s*[,}])' + '|' +
      // Value in object: ':' followed by value followed by ',' or '}'
      '([\\[,]\\s*)(' + valueRegex + ')(?=\\s*[\\],])' +
      // Value in array: '[' or ',' followed by value followed by ',' or ']'
      ')';
      return JSONString.replace(new RegExp(regex, 'gm'), (full, p1, p2, p3, p4) => {
        const startPart = p1 ? p1 : p3;
        const value = p2 ? p2 : p4;
        return startPart + callbackFn(value);
      });
    }
    /**
     * Decodes all 'normal' strings without special meaning in a JSON-like object
     *
     * @param jsonLevel - The current level of parsing
     * @param unescapeStrings - Whether to unescape the decoded strings as well
     */
    decodeJSONStrings(jsonLevel, unescapeStrings = true) {
      for (const prop in jsonLevel) {
        if (typeof jsonLevel[prop] === 'string') {
          // Ignore var placeholders
          if (jsonLevel[prop] === '__EVENT__"' || jsonLevel[prop].match(new RegExp('^\\s*' + regexes.placeholderContextVariableRegex + '\\s*$', 'gm'))) {
            continue;
          }
          // Otherwise decode string
          let decodedString = this.decodeDataTypeString(jsonLevel[prop]);
          decodedString = unescapeStrings ? this.dataTypeEncoder.stripSlashes(decodedString) : decodedString;
          jsonLevel[prop] = decodedString;
        } else if (typeof jsonLevel[prop] === 'object') {
          this.decodeJSONStrings(jsonLevel[prop], unescapeStrings);
        }
      }
    }
    // Loading variables
    // ----------------------------------------------------------------------------------------------------------------------------------------
    /**
     * Takes a parsed input data type, looks for variable placeholder strings and replaces them with the actual variables
     *
     * IMPORTANT: To correctly find variables, their substrings, subfunction and subbrackets must be encoded (done in evaluate())
     *
     * @param input - The input variable to check
     * @param context - The current context object, if any
     * @param event - The current event object, if any
     * @param unescapeStrings - Whether to unescape strings or not
     * @param trackContextVariables - An optional object that will be filled out with all found context vars
     * @param allowContextFunctionCalls - Whether function calls in context vars are allowed
     */
    loadVariables(input, context = {}, event, unescapeStrings = true, trackContextVariables = {}, allowContextFunctionCalls = true) {
      const wrapper = {
        result: input
      };
      this.loadVariablesLoop(wrapper, context, event, unescapeStrings, trackContextVariables, allowContextFunctionCalls);
      return wrapper.result;
    }
    /**
     * Travels a JSON-like object to find all context vars and event objects and replaces their placeholders with the actual values
     *
     * @param arrayOrObject - The property of the JSON to analyze
     * @param context - The current context object, if any
     * @param event - The current event object, if any
     * @param unescapeStrings - Whether to unescape strings or not
     * @param trackContextVariables - Whether to unescape strings or not
     * @param allowContextFunctionCalls - Whether function calls in context vars are allowed
     */
    loadVariablesLoop(arrayOrObject, context = {}, event, unescapeStrings = true, trackContextVariables = {}, allowContextFunctionCalls = true) {
      for (const prop in arrayOrObject) {
        // Only interested in strings
        if (typeof arrayOrObject[prop] === 'string') {
          // If event placeholder
          if (arrayOrObject[prop] === '__EVENT__') {
            arrayOrObject[prop] = event;
          } else
            // If context var placeholder
            if (arrayOrObject[prop].match(new RegExp('^\\s*' + regexes.placeholderContextVariableRegex + '\\s*$', 'gm'))) {
              const contextVar = this.dataTypeEncoder.transformPlaceholderIntoContextVar(arrayOrObject[prop].trim());
              arrayOrObject[prop] = this.safelyLoadContextVariable(contextVar, context, event, unescapeStrings, trackContextVariables, allowContextFunctionCalls);
            }
        } else if (typeof arrayOrObject[prop] === 'object') {
          this.loadVariablesLoop(arrayOrObject[prop], context, event, unescapeStrings, trackContextVariables);
        }
      }
    }
    /**
     * A safe wrapper around the loadContextVariable function. Returns undefined if there is any error.
     *
     * @param contextVar - The context var
     * @param context - The context object
     * @param event - An event object, if available
     * @param unescapeStrings - Whether to unescape strings or not
     * @param trackContextVariables - An optional object that will be filled out with all found context vars
     * @param allowContextFunctionCalls - Whether function calls in context vars are allowed
     */
    safelyLoadContextVariable(contextVar, context = {}, event, unescapeStrings = true, trackContextVariables = {}, allowContextFunctionCalls = true) {
      try {
        const resolvedContextVariable = this.loadContextVariable(contextVar, context, event, unescapeStrings, trackContextVariables, allowContextFunctionCalls);
        trackContextVariables[this.decodeDataTypeString(contextVar)] = resolvedContextVariable;
        return resolvedContextVariable;
      } catch (e) {
        if (isDevMode()) {
          console.warn(e);
        }
        trackContextVariables[this.decodeDataTypeString(contextVar)] = undefined;
        return undefined;
      }
    }
    /**
     * Takes a context variable string and evaluates it to get the desired value
     *
     * IMPORTANT: To correctly parse variables, their substrings, subfunction and subbrackets must be encoded (done in evaluate())
     *
     * @param contextVar - The context var
     * @param context - The context object
     * @param event - An event object, if available
     * @param unescapeStrings - Whether to unescape strings or not
     * @param trackContextVariables - An optional object that will be filled out with all found context vars
     * @param allowContextFunctionCalls - Whether function calls in context vars are allowed
     */
    loadContextVariable(contextVar, context = {}, event, unescapeStrings = true, trackContextVariables = {}, allowContextFunctionCalls = true) {
      const shortContextVar = contextVar.substr(7); // Cut off 'context' from the front
      // If context object is requested directly
      if (shortContextVar.trim() === '') {
        return context;
      }
      // Otherwise, create variable path array and fetch value, so the context object can be easily travelled.
      // Variable path example: 'restaurants["newOrleans"].reviews[5]' becomes ['restaurants', 'newOrleans', 'reviews', 5],
      const path = [];
      const pathMatches = matchAll(shortContextVar, new RegExp(regexes.variablePathPartRegex, 'gm'));
      for (const match of pathMatches) {
        // 1. If dot notation
        if (match[0].startsWith('.')) {
          path.push({
            type: 'property',
            value: match[0].substr(1)
          });
        }
        // 2. If bracket notation
        if (match[0].startsWith('[') && match[0].endsWith(']')) {
          let bracketValue = match[0].substr(1, match[0].length - 2);
          // Evaluate bracket parameter
          bracketValue = this.decodeDataTypeString(bracketValue); // Decode variable
          bracketValue = this.evaluate(bracketValue, context, event, unescapeStrings, trackContextVariables, allowContextFunctionCalls); // Recursively repeat the process
          path.push({
            type: 'property',
            value: bracketValue
          });
        }
        // 3. If function call
        if (match[0].startsWith('(') && match[0].endsWith(')')) {
          // Check if function calls are allowed
          if (!allowContextFunctionCalls) {
            throw Error('Tried to call a function in a context variable. This has been disallowed in the current config.');
          }
          const funcParams = match[0].substr(1, match[0].length - 2); // Strip outer brackets
          // Evaluate function parameters
          const paramsArray = [];
          if (funcParams !== '') {
            for (const param of funcParams.split(',')) {
              let p = this.decodeDataTypeString(param); // Decode variable
              p = this.evaluate(p, context, event, unescapeStrings, trackContextVariables, allowContextFunctionCalls); // Recursively repeat the process
              paramsArray.push(p);
            }
          }
          // Add function to path
          path.push({
            type: 'function',
            value: paramsArray
          });
        }
      }
      try {
        return this.fetchContextVariable(context, path);
      } catch (e) {
        if (isDevMode()) {
          console.warn(e);
        }
        throw Error('The required context variable "' + this.decodeDataTypeString(contextVar) + '" could not be found in the context object. Returning undefined instead.');
      }
    }
    /**
     * Recursively travels an object with the help of a path array and returns the specified value,
     * or undefined if not found
     *
     * @param contextLevel - The object to travel
     * @param path - The property path array
     */
    fetchContextVariable(contextLevel, path) {
      // Prevent accessing protected properties
      if (path[0].value === '__proto__') {
        throw Error('Accessing the __proto__ property through a context variable is not allowed.');
      }
      if (path[0].value === 'prototype') {
        throw Error('Accessing the prototype property through a context variable is not allowed.');
      }
      if (path[0].value === 'constructor') {
        throw Error('Accessing the constructor property through a context variable is not allowed.');
      }
      if (contextLevel === undefined) {
        throw Error('Context variable path could not be resolved. Trying to access ' + (path[0].type === 'property' ? 'property "' + path[0].value + '" of undefined.' : 'undefined function.'));
      }
      // Get property
      let result;
      if (path[0].type === 'property') {
        if (contextLevel.hasOwnProperty(path[0].value)) {
          result = contextLevel[path[0].value];
          // It makes a difference to JavaScript whether you call a function by 'obj.func()' or by 'let func = obj.func; func();'
          // In the latter case, 'this' will be undefined and not point to the parent. Since this recursive approach uses that latter version,
          // manually bind each function to the parent to restore the normal behavior.
          // Also: If the user has submitted a bound function himself, calling .bind here again does nothing, which is the desired behaviour.
          if (typeof result === 'function') {
            result = result.bind(contextLevel);
          }
          // Check '__proto__' as well as functions tend to live here instead of directly on the instance
        } else if (contextLevel.__proto__.hasOwnProperty(path[0].value)) {
          result = contextLevel.__proto__[path[0].value];
          if (typeof result === 'function') {
            result = result.bind(contextLevel);
          }
        } else {
          result = undefined;
        }
      } else if (path[0].type === 'function') {
        result = contextLevel(...path[0].value);
      }
      path.shift();
      // Recursively travel path
      if (path.length > 0) {
        result = this.fetchContextVariable(result, path);
      }
      return result;
    }
  }
  DataTypeParser.ɵfac = function DataTypeParser_Factory(t) {
    return new (t || DataTypeParser)(i0.ɵɵinject(DataTypeEncoder));
  };
  DataTypeParser.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: DataTypeParser,
    factory: DataTypeParser.ɵfac
  });
  return DataTypeParser;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * A service for the SelectorHookParser, responsible for creating @Input()/@Output() bindings from opening tags and updating them,
 * keeping all previous references, if possible
 */
let BindingStateManager = /*#__PURE__*/(() => {
  class BindingStateManager {
    constructor(dataTypeParser) {
      this.dataTypeParser = dataTypeParser;
    }
    // 1. Inputs
    // -------------------------------------------------------------------------------------
    /**
     * Creates or updates the current input bindings
     *
     * @param openingTag - The opening tag to analyze
     * @param context - The current context object
     * @param parserConfig - The parser config
     * @param currentInputBindings - The previous @Input() bindings, if existing
     */
    getCurrentInputBindings(openingTag, context, parserConfig, currentInputBindings) {
      // If this is initial evaluation, create bindings object
      if (currentInputBindings === undefined) {
        currentInputBindings = this.createInputBindings(openingTag, context, parserConfig);
        // If this is subsequent evaluation, update bindings object
      } else {
        this.updateInputBindings(currentInputBindings, context, parserConfig);
      }
      return currentInputBindings;
    }
    /**
     * Creates the input bindings object on the first evaluation of a hook
     *
     * @param openingTag - The opening tag to analyze
     * @param context - The current context object
     * @param parserConfig - The current parser config
     */
    createInputBindings(openingTag, context, parserConfig) {
      // Read inputs from opening tag
      const rawNoBracketInputs = this.getBindingsFromOpeningTag('noBracketInputs', openingTag, parserConfig.inputsBlacklist, parserConfig.inputsWhitelist);
      const rawBracketInputs = this.getBindingsFromOpeningTag('bracketInputs', openingTag, parserConfig.inputsBlacklist, parserConfig.inputsWhitelist);
      // NoBracketInputs are to be interpreted as plain strings, so wrap them in quotes
      for (const [noBracketInputName, noBracketInputValue] of Object.entries(rawNoBracketInputs)) {
        rawNoBracketInputs[noBracketInputName] = "'" + noBracketInputValue + "'";
      }
      // Merge both input objects
      const mergedRawInputs = {
        ...rawNoBracketInputs,
        ...rawBracketInputs
      };
      // Create bindings object
      const inputBindings = {};
      for (const [rawInputKey, rawInputValue] of Object.entries(mergedRawInputs)) {
        inputBindings[rawInputKey] = {
          raw: rawInputValue,
          value: rawInputValue,
          boundContextVariables: {}
        };
      }
      // If no need to parse, return result immediately
      if (!parserConfig.parseInputs) {
        return inputBindings;
      }
      // Parse input strings and track all bound context variables
      for (const [inputName, inputBinding] of Object.entries(inputBindings)) {
        try {
          inputBindings[inputName].value = this.dataTypeParser.evaluate(inputBinding.raw, parserConfig.allowContextInBindings ? context : {}, undefined, parserConfig.unescapeStrings, inputBindings[inputName].boundContextVariables, parserConfig.allowContextFunctionCalls);
        } catch (e) {
          if (isDevMode()) {
            console.error(`Hook input parsing error\nselector: ` + parserConfig.selector + `\ninput: ` + inputName + `\nvalue: "` + inputBinding.value + `"`);
            console.error(e.stack);
          }
          // If binding could not be parsed at all due to syntax error, remove from list of inputs.
          // No amount of calls to updateInputBindings() will fix this kind of error.
          delete inputBindings[inputName];
        }
      }
      return inputBindings;
    }
    /**
     * Updates the input bindings object on subsequent evaluations of a hook
     *
     * We can detect if a binding needs to be reevaluated via the bound context variables. There are three cases to consider:
     *
     * a) If a binding does not use context vars, don't reevaluate (binding is static and won't ever need to be updated)
     * b) If a binding does use context vars, but context vars haven't changed, don't reevaluate either (would evalute the same)
     * c) If a binding uses context vars and they have changed, reevaluate the binding from scratch to get the new version
     *
     * This is in line with the standard Angular behavior when evaluating template vars like [input]="{prop: this.something}".
     * When 'this.something' changes so that it returns false on a === comparison with its previous value, Angular does not
     * simply replace the reference bound to 'prop', but recreates the whole object literal and passes a new reference into the
     * input, triggering ngOnChanges.
     *
     * @param currentInputBindings - The previous @Input() bindings
     * @param context - The current context object
     * @param parserConfig - The current parser config
     */
    updateInputBindings(currentInputBindings, context, parserConfig) {
      for (const inputBinding of Object.values(currentInputBindings)) {
        if (Object.keys(inputBinding.boundContextVariables).length > 0) {
          // Check if bound context vars have changed
          let boundContextVarHasChanged = false;
          for (const [contextVarName, contextVarValue] of Object.entries(inputBinding.boundContextVariables)) {
            const encodedContextVarName = this.dataTypeParser.encodeDataTypeString(contextVarName);
            // Compare with previous value
            const newContextVarValue = this.dataTypeParser.safelyLoadContextVariable(encodedContextVarName, context, undefined, parserConfig.unescapeStrings, {}, parserConfig.allowContextFunctionCalls);
            if (newContextVarValue !== contextVarValue) {
              boundContextVarHasChanged = true;
              break;
            }
          }
          // Bound context var has changed! Reevaluate binding
          if (boundContextVarHasChanged) {
            inputBinding.boundContextVariables = {};
            inputBinding.value = this.dataTypeParser.evaluate(inputBinding.raw, parserConfig.allowContextInBindings ? context : {}, undefined, parserConfig.unescapeStrings, inputBinding.boundContextVariables, parserConfig.allowContextFunctionCalls);
          }
        }
      }
    }
    // 2. Outputs
    // -------------------------------------------------------------------------------------
    /**
     * Creates the current output bindings
     *
     * @param openingTag - The opening tag to analyze
     * @param parserConfig - The parser config
     * @param currentOutputBindings - The previous @Output() bindings, if existing
     */
    getCurrentOutputBindings(openingTag, parserConfig, currentOutputBindings) {
      // As opposed to inputs, outputs only need to be created once by the parser, never updated, as this process only
      // consists of wrapping the data type evaluation into an outer function so it can be executed later when the
      // output triggers, in which case the data type will always be evaluated fresh. There is no need to replace
      // this outer function on updates.
      if (currentOutputBindings === undefined) {
        currentOutputBindings = this.createOutputBindings(openingTag, parserConfig);
      }
      return currentOutputBindings;
    }
    /**
     * Takes a standard hook opening tag and parses Angular-style inputs from it
     *
     * @param openingTag - The hook opening tag to parse
     * @param parserConfig - The current parser config
     */
    createOutputBindings(openingTag, parserConfig) {
      const rawOutputs = this.getBindingsFromOpeningTag('outputs', openingTag, parserConfig.outputsBlacklist, parserConfig.outputsWhitelist);
      // Create bindings object
      const outputBindings = {};
      for (const [rawOutputKey, rawOutputValue] of Object.entries(rawOutputs)) {
        outputBindings[rawOutputKey] = {
          raw: rawOutputValue,
          value: rawOutputValue,
          boundContextVariables: {}
        };
      }
      // Create output callback functions
      for (const [outputName, outputBinding] of Object.entries(outputBindings)) {
        // Simply wrap evaluate() into outer function that will be called when the corresponding event emits.
        // If the dataTypeString contains a function, it will therefore be evaluated and executed "just-in-time".
        // If it contains a variable, nothing will happen as the the evaluated dataTypeString is not assigned anywhere and simply discarded.
        outputBindings[outputName].value = (event, context) => {
          try {
            this.dataTypeParser.evaluate(outputBinding.raw, parserConfig.allowContextInBindings ? context : {}, event, parserConfig.unescapeStrings, outputBinding.boundContextVariables, parserConfig.allowContextFunctionCalls);
          } catch (e) {
            if (isDevMode()) {
              console.error(`Hook output parsing error\nselector: ` + parserConfig.selector + `\noutput: ` + outputName + `\nvalue: "` + outputBinding.value + `"`);
              console.error(e.stack);
            }
          }
        };
      }
      return outputBindings;
    }
    // 3. Shared
    // -------------------------------------------------------------------------------------
    /**
     * Takes a standard hook opening tag and parses Angular-style bindings from it
     *
     * @param type - What kind of bindings to extract
     * @param openingTag - The opening tag to analyze
     * @param blacklist - What bindings to exlude
     * @param whitelist - What bindings to exclusively include
     */
    getBindingsFromOpeningTag(type, openingTag, blacklist, whitelist) {
      const bindings = {};
      // Examples: https://regex101.com/r/17x3cc/16
      const attributeValuesOR = '(?:' + regexes.attributeValueDoubleQuotesRegex + '|' + regexes.attributeValueSingleQuotesRegex + ')';
      let attributeNameRegex;
      switch (type) {
        case 'noBracketInputs':
          attributeNameRegex = regexes.attributeNameNoBracketsRegex;
          break;
        case 'bracketInputs':
          attributeNameRegex = regexes.attributeNameBracketsRegex;
          break;
        case 'outputs':
          attributeNameRegex = regexes.attributeNameRoundBracketsRegex;
          break;
      }
      const attributeRegex = attributeNameRegex + '\=' + attributeValuesOR;
      const attributePattern = new RegExp(attributeRegex, 'gim');
      const attributeMatches = matchAll(openingTag, attributePattern);
      // Collect raw bindings
      for (const match of attributeMatches) {
        // Could be either of the attribute value capturing groups
        let rawBindingValue = match[2] || match[3];
        // If value is empty (someInput=""), it will return undefined for it. When using noBracketInputs, return empty string instead.
        if (rawBindingValue === undefined && type === 'noBracketInputs') {
          rawBindingValue = '';
        }
        bindings[match[1]] = rawBindingValue;
      }
      // Filter bindings
      const filteredBindings = {};
      for (const [bindingName, bindingValue] of Object.entries(bindings)) {
        if (blacklist && blacklist.includes(bindingName)) {
          continue;
        }
        if (whitelist && !whitelist.includes(bindingName)) {
          continue;
        }
        filteredBindings[bindingName] = bindingValue;
      }
      return filteredBindings;
    }
  }
  BindingStateManager.ɵfac = function BindingStateManager_Factory(t) {
    return new (t || BindingStateManager)(i0.ɵɵinject(DataTypeParser));
  };
  BindingStateManager.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: BindingStateManager,
    factory: BindingStateManager.ɵfac
  });
  return BindingStateManager;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * A helper class for resolving HookParserEntries
 */
let ParserEntryResolver = /*#__PURE__*/(() => {
  class ParserEntryResolver {
    constructor(injector, parserResolver, selectorFinder, bindingStateManager) {
      this.injector = injector;
      this.parserResolver = parserResolver;
      this.selectorFinder = selectorFinder;
      this.bindingStateManager = bindingStateManager;
    }
    /**
     * Takes a list of HookParserEntries and transforms them into a list of loaded HookParsers
     *
     * @param parserEntries - The list of HookParserEntries to process
     * @param injector - The injector to use for resolving parsers
     * @param blacklist - (optional) Which parsers to blacklist by name
     * @param whitelist - (optional) Which parsers to whitelist by name
     */
    resolve(parserEntries, injector, blacklist, whitelist) {
      // Load all requested parsers
      const parsers = [];
      for (const parser of parserEntries) {
        const resolvedParser = this.resolveEntry(parser, injector);
        if (resolvedParser) {
          parsers.push(resolvedParser);
        }
      }
      // Check parser functions
      const validParsers = this.validateParserFunctions(parsers);
      // Check parser names
      this.checkParserNames(validParsers);
      // If no need to filter, return resolved parsers
      if (!blacklist && !whitelist) {
        return validParsers;
      }
      // Check black/whitelist
      this.checkBlackAndWhitelist(validParsers, blacklist, whitelist);
      // Filter parsers
      const filteredParsers = [];
      for (const validParser of validParsers) {
        if (validParser.hasOwnProperty('name') && typeof validParser.name === 'string') {
          if (blacklist && blacklist.includes(validParser.name)) {
            continue;
          }
          if (whitelist && !whitelist.includes(validParser.name)) {
            continue;
          }
        }
        filteredParsers.push(validParser);
      }
      return filteredParsers;
    }
    /**
     * Figures out what kind of config object (out of all possible types) the HookParserEntry is and loads it appropriately.
     *
     * The potential types are:
     * - a service
     * - a class
     * - an instance
     * - an object literal to configure SelectorHookParser with
     *
     * @param parserEntry - The HookParserEntry to process
     * @param injector - The injector to use for resolving this parser
     */
    resolveEntry(parserEntry, injector) {
      // Check if class
      if (parserEntry.hasOwnProperty('prototype')) {
        // Check if service
        try {
          return injector.get(parserEntry);
          // Otherwise instantiate manually
        } catch (e) {
          return new parserEntry();
        }
      }
      // Check if object
      else if (typeof parserEntry === 'object') {
        // Is instance
        if (parserEntry.constructor.name !== 'Object') {
          return parserEntry;
          // Is object literal
        } else {
          try {
            return new SelectorHookParser(parserEntry, this.parserResolver, this.selectorFinder, this.bindingStateManager);
          } catch (e) {
            if (isDevMode()) {
              console.error('Invalid parser config - ', parserEntry, '\n', e);
            }
          }
        }
      } else {
        if (isDevMode()) {
          console.error('Invalid parser config - ', parserEntry);
        }
      }
    }
    /**
     * Makes sure that the parsers have all required functions
     *
     * @param parsers - The parsers in question
     */
    validateParserFunctions(parsers) {
      const validParsers = [];
      for (const parser of parsers) {
        if (typeof parser.findHooks !== 'function') {
          console.error('Submitted parser does not implement "findHooks()". Removing from list of active parsers:', parser);
          continue;
        }
        if (typeof parser.loadComponent !== 'function') {
          console.error('Submitted parser does not implement "loadComponent()". Removing from list of active parsers:', parser);
          continue;
        }
        if (typeof parser.getBindings !== 'function') {
          console.error('Submitted parser does not implement "getBindings()". Removing from list of active parsers:', parser);
          continue;
        }
        validParsers.push(parser);
      }
      return validParsers;
    }
    /**
     * Makes sure that all parser names are unique
     *
     * @param parsers - The parsers in question
     */
    checkParserNames(parsers) {
      const parserNames = parsers.map(entry => entry.name).filter(entry => entry !== undefined);
      const previousNames = [];
      const alreadyWarnedNames = [];
      for (const parserName of parserNames) {
        if (previousNames.includes(parserName) && !alreadyWarnedNames.includes(parserName)) {
          if (isDevMode()) {
            console.warn('Parser name "' + parserName + '" is not unique and appears multiple times in the list of active parsers.');
            alreadyWarnedNames.push(parserName);
          }
        }
        previousNames.push(parserName);
      }
    }
    /**
     * A black/whitelist validation function for the benefit of the user. Outputs warnings in the console if something is off.
     *
     * @param parsers - The parsers in question
     * @param blacklist - The blacklist in question
     * @param whitelist - The whitelist in question
     */
    checkBlackAndWhitelist(parsers, blacklist, whitelist) {
      const parserNames = parsers.map(entry => entry.name).filter(entry => entry !== undefined);
      if (blacklist) {
        for (const blacklistedParser of blacklist) {
          if (!parserNames.includes(blacklistedParser)) {
            if (isDevMode()) {
              console.warn('Blacklisted parser name "' + blacklistedParser + '" does not appear in the list of global parsers names. Make sure both spellings are identical.');
            }
          }
        }
      }
      if (whitelist) {
        for (const whitelistedParser of whitelist) {
          if (!parserNames.includes(whitelistedParser)) {
            if (isDevMode()) {
              console.warn('Whitelisted parser name "' + whitelistedParser + '" does not appear in the list of global parsers names. Make sure both spellings are identical.');
            }
          }
        }
      }
    }
  }
  ParserEntryResolver.ɵfac = function ParserEntryResolver_Factory(t) {
    return new (t || ParserEntryResolver)(i0.ɵɵinject(i0.Injector), i0.ɵɵinject(SelectorHookParserConfigResolver), i0.ɵɵinject(SelectorHookFinder), i0.ɵɵinject(BindingStateManager));
  };
  ParserEntryResolver.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: ParserEntryResolver,
    factory: ParserEntryResolver.ɵfac
  });
  return ParserEntryResolver;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * A helper class for resolving OutletOptions
 */
let OptionsResolver = /*#__PURE__*/(() => {
  class OptionsResolver {
    /**
     * Overwrites the default options with a (partial) OutletOptions object and returns the result
     *
     * @param uo - The (partial) OutletOptions object
     */
    resolve(uo) {
      const newOptions = JSON.parse(JSON.stringify(outletOptionDefaults));
      if (uo) {
        for (const [optionName, optionValue] of Object.entries(uo)) {
          if (optionName === 'sanitize' && typeof optionValue === 'boolean') {
            newOptions.sanitize = optionValue;
          } else if (optionName === 'convertHTMLEntities' && typeof optionValue === 'boolean') {
            newOptions.convertHTMLEntities = optionValue;
          } else if (optionName === 'fixParagraphTags' && typeof optionValue === 'boolean') {
            newOptions.fixParagraphTags = optionValue;
          } else if (optionName === 'updateOnPushOnly' && typeof optionValue === 'boolean') {
            newOptions.updateOnPushOnly = optionValue;
          } else if (optionName === 'compareInputsByValue' && typeof optionValue === 'boolean') {
            newOptions.compareInputsByValue = optionValue;
          } else if (optionName === 'compareOutputsByValue' && typeof optionValue === 'boolean') {
            newOptions.compareOutputsByValue = optionValue;
          } else if (optionName === 'compareByValueDepth' && typeof optionValue === 'number') {
            newOptions.compareByValueDepth = optionValue;
          } else if (optionName === 'ignoreInputAliases' && typeof optionValue === 'boolean') {
            newOptions.ignoreInputAliases = optionValue;
          } else if (optionName === 'ignoreOutputAliases' && typeof optionValue === 'boolean') {
            newOptions.ignoreOutputAliases = optionValue;
          } else if (optionName === 'acceptInputsForAnyProperty' && typeof optionValue === 'boolean') {
            newOptions.acceptInputsForAnyProperty = optionValue;
          } else if (optionName === 'acceptOutputsForAnyObservable' && typeof optionValue === 'boolean') {
            newOptions.acceptOutputsForAnyObservable = optionValue;
          } else {
            console.error('Invalid option with name "' + optionName + '" and value ', optionValue);
          }
        }
      }
      return newOptions;
    }
  }
  OptionsResolver.ɵfac = function OptionsResolver_Factory(t) {
    return new (t || OptionsResolver)();
  };
  OptionsResolver.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: OptionsResolver,
    factory: OptionsResolver.ɵfac
  });
  return OptionsResolver;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Extend this service to implement custom platform.
 */
class PlatformService {}

/**
 * The service responsible for finding all Hooks in the content, replacing them with component placeholders
 * and creating the HookIndex
 */
let HooksReplacer = /*#__PURE__*/(() => {
  class HooksReplacer {
    constructor(rendererFactory, platform) {
      this.platform = platform;
      this.renderer = rendererFactory.createRenderer(null, null);
    }
    // 1. Replacing hooks
    // -----------------------------------------------------------------------------------------------------------------------
    /**
     * Lets all registered parsers anaylyze the content to find all hooks within. Then replaces those hooks with component placeholder elements
     * (that will remain empty for now) and creates the hookIndex.
     *
     * It optionally also sanitizes the content and fixes paragraph artifacts.
     *
     * @param content - The text to parse
     * @param context - The current context object
     * @param parsers - All of the registered parsers
     * @param token - A random token to attach to all created selector elements
     * @param options - The current OutletOptions
     * @param hookIndex - The hookIndex object to fill
     */
    replaceHooksWithNodes(content, context, parsers, token, options, hookIndex) {
      let hookCount = 1;
      // Collect all parser results (before changing content), sort by startIndex
      let parserResults = [];
      for (const parser of parsers) {
        for (const hookPosition of parser.findHooks(content, context)) {
          parserResults.push({
            parser,
            hookPosition
          });
        }
      }
      parserResults.sort((a, b) => a.hookPosition.openingTagStartIndex - b.hookPosition.openingTagStartIndex);
      // Validate parser results
      parserResults = this.validateHookPositions(parserResults, content);
      // Process parser results to
      // a) Create an array of simple ReplaceInstructions to replace the hooks with the component placeholders
      // b) Enter each found hook into hookIndex
      // c) Replace tag artifacts
      const selectorReplaceInstructions = [];
      for (const pr of parserResults) {
        // Some info about this hook
        const hookSegments = this.getHookSegments(pr.hookPosition, content);
        // Create ReplaceInstructions array
        // Notes:
        // 1. Attach some parsing info as attributes to the placeholders for dynamically creating the components later
        // 2. Use encoded tag syntax to make them pass sanitization unnoticed (decoded again below after sanitization)
        // 3. Since the component selector of lazy-loaded components can't be known at this point, use placeholders tags for now (replaced with real selectors in ComponentCreator)
        // 4. Still use custom tags however to circumvent HTML nesting rules for established tags (browser might autocorrect nesting structure otherwise)
        selectorReplaceInstructions.push({
          startIndex: pr.hookPosition.openingTagStartIndex,
          endIndex: pr.hookPosition.openingTagEndIndex,
          replacement: this.encodeComponentPlaceholderElement('<dynamic-component-placeholder hookid="' + hookCount + '" parsetoken="' + token + '" ' + (pr.parser.name ? 'parser="' + pr.parser.name + '"' : '') + '>')
        });
        selectorReplaceInstructions.push({
          startIndex: hookSegments.enclosing ? pr.hookPosition.closingTagStartIndex : pr.hookPosition.openingTagEndIndex,
          endIndex: hookSegments.enclosing ? pr.hookPosition.closingTagEndIndex : pr.hookPosition.openingTagEndIndex,
          replacement: this.encodeComponentPlaceholderElement('</dynamic-component-placeholder>')
        });
        // Enter hook into index
        hookIndex[hookCount] = {
          id: hookCount,
          parser: pr.parser,
          value: {
            openingTag: hookSegments.openingTag,
            closingTag: hookSegments.closingTag
          },
          data: null,
          bindings: null,
          previousBindings: null,
          componentRef: null,
          dirtyInputs: new Set(),
          outputSubscriptions: {}
        };
        hookCount++;
        // Remove tag artifacts (does not change parser results indexes)
        if (hookSegments.enclosing && options.fixParagraphTags) {
          const firstResult = this.removeTagArtifacts(hookSegments.textBefore, '<p>', '</p>', hookSegments.innerValue, '</p>', '<p>');
          hookSegments.textBefore = firstResult.firstText;
          hookSegments.innerValue = firstResult.secondText;
          const secondResult = this.removeTagArtifacts(hookSegments.innerValue, '<p>', '</p>', hookSegments.textAfter, '</p>', '<p>');
          hookSegments.innerValue = secondResult.firstText;
          hookSegments.textAfter = secondResult.secondText;
          content = hookSegments.textBefore + hookSegments.openingTag + hookSegments.innerValue + hookSegments.closingTag + hookSegments.textAfter;
        }
      }
      // Sort replace instructions by startIndex so indexModifier only applies to indexes that follow, not precede
      selectorReplaceInstructions.sort((a, b) => a.startIndex - b.startIndex);
      // Replace found hooks with encoded component placeholders
      let indexModifier = 0;
      for (const selectorReplaceInstruction of selectorReplaceInstructions) {
        const textBeforeSelector = content.substr(0, selectorReplaceInstruction.startIndex + indexModifier);
        const textAfterSelector = content.substr(selectorReplaceInstruction.endIndex + indexModifier);
        const oldDynamicTextLength = content.length;
        // Reassemble and correct index
        content = textBeforeSelector + selectorReplaceInstruction.replacement + textAfterSelector;
        indexModifier += content.length - oldDynamicTextLength;
      }
      // Sanitize? (ignores the encoded component selector elements)
      if (options.sanitize) {
        content = this.platform.sanitize(content);
      }
      // Decode component selector elements again
      content = this.decodeComponentPlaceholderElements(content);
      return {
        content: content,
        hookIndex: hookIndex
      };
    }
    /**
     * Takes a HookPosition and returns the HookValue as well as the text surrounding it
     *
     * @param hookPosition - The HookPosition in question
     * @param content - The source text for the HookPosition
     */
    getHookSegments(hookPosition, content) {
      const enclosing = Number.isInteger(hookPosition.closingTagStartIndex) && Number.isInteger(hookPosition.closingTagEndIndex);
      return {
        enclosing: enclosing,
        textBefore: content.substr(0, hookPosition.openingTagStartIndex),
        openingTag: content.substr(hookPosition.openingTagStartIndex, hookPosition.openingTagEndIndex - hookPosition.openingTagStartIndex),
        innerValue: enclosing ? content.substr(hookPosition.openingTagEndIndex, hookPosition.closingTagStartIndex - hookPosition.openingTagEndIndex) : null,
        closingTag: enclosing ? content.substr(hookPosition.closingTagStartIndex, hookPosition.closingTagEndIndex - hookPosition.closingTagStartIndex) : null,
        textAfter: enclosing ? content.substr(hookPosition.closingTagEndIndex) : content.substr(hookPosition.openingTagEndIndex)
      };
    }
    /**
     * Checks the hookPositions of the combined parserResults to ensure they do not collide/overlap.
     * Any that do are removed from the results.
     *
     * @param parserResults - The parserResults from replaceHooksWithNodes()
     * @param content - The source text for the parserResults
     */
    validateHookPositions(parserResults, content) {
      const checkedParserResults = [];
      outerloop: for (const [index, parserResult] of parserResults.entries()) {
        const enclosing = Number.isInteger(parserResult.hookPosition.closingTagStartIndex) && Number.isInteger(parserResult.hookPosition.closingTagEndIndex);
        const hookPos = parserResult.hookPosition;
        // Check if hook is in itself well-formed
        if (hookPos.openingTagStartIndex >= hookPos.openingTagEndIndex) {
          if (isDevMode()) {
            console.warn('Error when checking hook positions - openingTagEndIndex has to be greater than openingTagStartIndex. Ignoring.', hookPos);
          }
          continue;
        }
        if (enclosing && hookPos.openingTagEndIndex > hookPos.closingTagStartIndex) {
          if (isDevMode()) {
            console.warn('Error when checking hook positions - The closing tag must start after the opening tag has concluded. Ignoring.', hookPos);
          }
          continue;
        }
        if (enclosing && hookPos.closingTagStartIndex >= hookPos.closingTagEndIndex) {
          if (isDevMode()) {
            console.warn('Error when checking hook positions - closingTagEndIndex has to be greater than closingTagStartIndex. Ignoring.', hookPos);
          }
          continue;
        }
        // Check if hook overlaps with other hooks
        const previousHooks = parserResults.slice(0, index);
        innerloop: for (const previousHook of previousHooks) {
          const prevHookPos = previousHook.hookPosition;
          const prevIsEnclosing = Number.isInteger(prevHookPos.closingTagStartIndex) && Number.isInteger(prevHookPos.closingTagEndIndex);
          // Check if identical hook position
          if (hookPos.openingTagStartIndex === prevHookPos.openingTagStartIndex && hookPos.openingTagEndIndex === prevHookPos.openingTagEndIndex && (!enclosing || !prevIsEnclosing || hookPos.closingTagStartIndex === prevHookPos.closingTagStartIndex && hookPos.closingTagEndIndex === prevHookPos.closingTagEndIndex)) {
            this.generateHookPosWarning('A hook with the same position as another hook was found. There may be multiple identical parsers active that are looking for the same hook. Ignoring duplicates.', hookPos, prevHookPos, content);
            continue outerloop;
          }
          // Opening tag must begin after previous opening tag has ended
          if (hookPos.openingTagStartIndex < prevHookPos.openingTagEndIndex) {
            this.generateHookPosWarning('Error when checking hook positions: Hook opening tag starts before previous hook opening tag ends. Ignoring.', hookPos, prevHookPos, content);
            continue outerloop;
          }
          // Opening tag must not overlap with previous closing tag
          if (prevIsEnclosing && !(hookPos.openingTagEndIndex <= prevHookPos.closingTagStartIndex || hookPos.openingTagStartIndex >= prevHookPos.closingTagEndIndex)) {
            this.generateHookPosWarning('Error when checking hook positions: Opening tag of hook overlaps with closing tag of previous hook. Ignoring.', hookPos, prevHookPos, content);
            continue outerloop;
          }
          // Closing tag must not overlap with previous closing tag
          if (prevIsEnclosing && enclosing && !(hookPos.closingTagEndIndex <= prevHookPos.closingTagStartIndex || hookPos.closingTagStartIndex >= prevHookPos.closingTagEndIndex)) {
            this.generateHookPosWarning('Error when checking hook positions: Closing tag of hook overlaps with closing tag of previous hook. Ignoring.', hookPos, prevHookPos, content);
            continue outerloop;
          }
          // Check if hooks are incorrectly nested, e.g. "<outer-hook><inner-hook></outer-hook></inner-hook>"
          if (enclosing && prevIsEnclosing && hookPos.openingTagEndIndex <= prevHookPos.closingTagStartIndex && hookPos.closingTagStartIndex >= prevHookPos.closingTagEndIndex) {
            this.generateHookPosWarning('Error when checking hook positions: The closing tag of a nested hook lies beyond the closing tag of the outer hook. Ignoring.', hookPos, prevHookPos, content);
            continue outerloop;
          }
        }
        // If everything okay, add to result array
        checkedParserResults.push(parserResult);
      }
      return checkedParserResults;
    }
    /**
     * Outputs a warning in the console when the positions of two hooks are invalid in some manner
     *
     * @param message - The error message
     * @param hookPos - The first HookPosition
     * @param prevHookPos - The second HookPosition
     * @param content - The source text for the HookPositions
     */
    generateHookPosWarning(message, hookPos, prevHookPos, content) {
      if (isDevMode()) {
        const prevHookSegments = this.getHookSegments(prevHookPos, content);
        const hookSegments = this.getHookSegments(hookPos, content);
        const prevHookData = {
          openingTag: prevHookSegments.openingTag,
          openingTagStartIndex: prevHookPos.openingTagStartIndex,
          openingTagEndIndex: prevHookPos.openingTagEndIndex,
          closingTag: prevHookSegments.closingTag,
          closingTagStartIndex: prevHookPos.closingTagStartIndex,
          closingTagEndIndex: prevHookPos.closingTagEndIndex
        };
        const hookData = {
          openingTag: hookSegments.openingTag,
          openingTagStartIndex: hookPos.openingTagStartIndex,
          openingTagEndIndex: hookPos.openingTagEndIndex,
          closingTag: hookSegments.closingTag,
          closingTagStartIndex: hookPos.closingTagStartIndex,
          closingTagEndIndex: hookPos.closingTagEndIndex
        };
        console.warn(message + '\nFirst hook: ', prevHookData, '\nSecond hook:', hookData);
      }
    }
    /**
     * When using an enclosing hook that is spread over several lines in an HTML editor, p-elements tend to get ripped apart. For example:
     *
     * <p><app-hook></p>
     *   <h2>This is the hook content</h2>
     * <p></app-hook></p>
     *
     * would cause the innerValue of app-hook to have a lone closing and opening p-tag (as their counterparts are outside of the hook).
     * To clean up the HTML, this function removes a pair of these artifacts (e.g. <p> before hook, </p> inside hook) if BOTH are found.
     * This is important as the HTML parser will otherwise mess up the intended HTML and sometimes even put what should be ng-content below the component.
     *
     * @param firstText - The text on one side of the hook
     * @param firstArtifact - A string that should be removed from firstText...
     * @param firstRemoveIfAfter - ...if it appears after the last occurrence of this string
     * @param secondText - The text on the other side of the hook
     * @param secondArtifact - A string that should be removed from secondText...
     * @param secondRemoveIfBefore - ...if it appears before the first occurrence of this string
     */
    removeTagArtifacts(firstText, firstArtifact, firstRemoveIfAfter, secondText, secondArtifact = null, secondRemoveIfBefore) {
      let firstArtifactFound = false;
      let secondArtifactFound = false;
      // a) Look for first artifact
      const firstArtifactIndex = firstText.lastIndexOf(firstArtifact);
      const firstArtifactIfAfterIndex = firstText.lastIndexOf(firstRemoveIfAfter);
      if (firstArtifactIndex >= 0 && firstArtifactIfAfterIndex === -1 || firstArtifactIndex > firstArtifactIfAfterIndex) {
        firstArtifactFound = true;
      }
      // b) Look for second artifact
      const secondArtifactIndex = secondText.indexOf(secondArtifact);
      const secondArtifactIfBeforeIndex = secondText.indexOf(secondRemoveIfBefore);
      if (
      // If startArtifact appears before startArtifactNotBefore
      secondArtifactIndex >= 0 && secondArtifactIfBeforeIndex === -1 || secondArtifactIndex < secondArtifactIfBeforeIndex) {
        secondArtifactFound = true;
      }
      // If artifacts found on both sides, remove both by overwriting them with empty spaces (doesn't change index)
      if (firstArtifactFound && secondArtifactFound) {
        firstText = firstText.substr(0, firstArtifactIndex) + ' '.repeat(firstArtifact.length) + firstText.substr(firstArtifactIndex + firstArtifact.length);
        secondText = secondText.substr(0, secondArtifactIndex) + ' '.repeat(secondArtifact.length) + secondText.substr(secondArtifactIndex + secondArtifact.length);
      }
      // Trim after artifacts removed
      return {
        firstText: firstText,
        secondText: secondText
      };
    }
    /**
     * Encodes the special html chars in a component placeholder tag
     *
     * @param element - The placeholder element as a string
     */
    encodeComponentPlaceholderElement(element) {
      element = element.replace(/</g, '@@@hook-lt@@@');
      element = element.replace(/>/g, '@@@hook-gt@@@');
      element = element.replace(/"/g, '@@@hook-dq@@@');
      return element;
    }
    /**
     * Decodes the special html chars in a component placeholder tag
     *
     * @param element - The placeholder element as a string
     */
    decodeComponentPlaceholderElements(element) {
      element = element.replace(/@@@hook-lt@@@/g, '<');
      element = element.replace(/@@@hook-gt@@@/g, '>');
      element = element.replace(/@@@hook-dq@@@/g, '"');
      return element;
    }
    /**
     * Converts all HTML entities to normal characters
     *
     * @param text - The text with the potential HTML entities
     */
    convertHTMLEntities(text) {
      const div = this.renderer.createElement('div');
      const result = text.replace(/&[#A-Za-z0-9]+;/gi, hmtlEntity => {
        // Replace invisible nbsp-whitespace with normal whitespace (not \u00A0). Leads to problems with JSON.parse() otherwise.
        if (hmtlEntity === '&nbsp;') {
          return ' ';
        }
        this.platform.setInnerContent(div, hmtlEntity);
        return this.platform.getInnerText(div);
      });
      return result;
    }
  }
  HooksReplacer.ɵfac = function HooksReplacer_Factory(t) {
    return new (t || HooksReplacer)(i0.ɵɵinject(i0.RendererFactory2), i0.ɵɵinject(PlatformService));
  };
  HooksReplacer.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: HooksReplacer,
    factory: HooksReplacer.ɵfac
  });
  return HooksReplacer;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * This services can be used to compare two variables by value instead of reference
 */
let DeepComparer = /*#__PURE__*/(() => {
  class DeepComparer {
    // 1. Inputs
    // -----------------------------------------------------------------
    constructor() {}
    /**
     * Tests if two objects are equal by value
     *
     * @param a - The first object
     * @param b - The second object
     * @param compareDepth - How many levels deep to compare
     */
    isEqual(a, b, compareDepth) {
      const aStringified = this.detailedStringify(a, compareDepth);
      const bStringified = this.detailedStringify(b, compareDepth);
      if (aStringified.result === null || bStringified.result === null) {
        if (isDevMode) {
          console.warn('Objects could not be compared by value as one or both of them could not be stringified. Returning false. \n', 'Objects:', a, b);
        }
        return false;
      }
      return aStringified.result === bStringified.result;
    }
    /**
     * Like JSON.stringify, but stringifies additional datatypes that would have been
     * nulled otherwise. It also doesn't throw errors on cyclic property paths.
     *
     * If obj can't be stringified for whatever reason, returns null.
     *
     * @param obj - The object to stringify
     * @param depth - How many levels deep to stringify
     */
    detailedStringify(obj, depth) {
      try {
        // Null cyclic paths
        const depthReached = {
          count: 0
        };
        const decylcedObj = this.decycle(obj, [], depth, depthReached);
        const stringified = JSON.stringify(decylcedObj, (key, value) => {
          // If undefined
          if (value === undefined) {
            return 'undefined';
          }
          // If function or class
          if (typeof value === 'function') {
            return value.toString();
          }
          // If symbol
          if (typeof value === 'symbol') {
            return value.toString();
          }
          return value;
        });
        return {
          result: stringified,
          depthReachedCount: depthReached.count
        };
      } catch (e) {
        return {
          result: null,
          depthReachedCount: 0
        };
      }
    }
    /**
     * Travels on object and replaces cyclical references with null
     *
     * @param obj - The object to travel
     * @param stack - To keep track of already travelled objects
     * @param depth - How many levels deep to decycle
     * @param depthReached - An object to track the number of times the max depth was reached
     */
    decycle(obj, stack = [], depth = 5, depthReached) {
      if (stack.length > depth) {
        depthReached.count++;
        return null;
      }
      if (!obj || typeof obj !== 'object' || obj instanceof Date) {
        return obj;
      }
      // Check if cyclical and we've traveled this obj already
      //
      // Note: Test this not by object reference, but by object PROPERTY reference/equality. If an object has identical properties,
      // the object is to be considered identical even if it has a different reference itself.
      //
      // Explanation: This is to prevent a sneaky bug when comparing by value and a parser returns an object as an input that contains a reference to the object holding it
      // (like returning the context object that contains a reference to the parent component holding the context object).
      // In this example, when the context object changes by reference, the old input will be compared with the new input. However, as the old input consists of
      // the old context object that now (through the parent component) contains a reference to the new context object, while the new input references the new context
      // object exclusively, the decycle function would produce different results for them if it only checked cyclical paths by reference (even if the context object
      // remained identical in value!)
      //
      // Though an unlikely scenario, checking cyclical paths via object properties rather than the object reference itself solves this problem.
      for (const stackObj of stack) {
        if (this.objEqualsProperties(obj, stackObj)) {
          return null;
        }
      }
      const s = stack.concat([obj]);
      if (Array.isArray(obj)) {
        const newArray = [];
        for (const entry of obj) {
          newArray.push(this.decycle(entry, s, depth, depthReached));
        }
        return newArray;
      } else {
        const newObj = {};
        for (const key of Object.keys(obj)) {
          newObj[key] = this.decycle(obj[key], s, depth, depthReached);
        }
        return newObj;
      }
    }
    /**
     * Returns true when all the properties of one object equal those of another object, otherwise false.
     *
     * @param a - The first object
     * @param b - The second object
     */
    objEqualsProperties(a, b) {
      const aKeys = Object.keys(a);
      const bKeys = Object.keys(b);
      if (aKeys.length !== bKeys.length) {
        return false;
      }
      for (const aKey of aKeys) {
        if (a[aKey] !== b[aKey]) {
          return false;
        }
      }
      return true;
    }
  }
  DeepComparer.ɵfac = function DeepComparer_Factory(t) {
    return new (t || DeepComparer)();
  };
  DeepComparer.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: DeepComparer,
    factory: DeepComparer.ɵfac
  });
  return DeepComparer;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * The service responsible for updating dynamically created components
 */
let ComponentUpdater = /*#__PURE__*/(() => {
  class ComponentUpdater {
    constructor(cfr, deepComparer) {
      this.cfr = cfr;
      this.deepComparer = deepComparer;
    }
    /**
     * Invoked when the inputs/outputs should be checked for updates
     *
     * @param hookIndex - The current hookIndex
     * @param context - The new context object
     * @param options - The current OutletOptions
     * @param triggerOnDynamicChanges - Whether to trigger the OnDynamicChanges method of dynamically loaded components
     */
    refresh(hookIndex, context, options, triggerOnDynamicChanges) {
      for (const [hookId, hook] of Object.entries(hookIndex)) {
        // Save previous bindings
        hook.previousBindings = {
          inputs: this.savePreviousBindings(hook, 'inputs', options.compareInputsByValue, options.compareByValueDepth),
          outputs: this.savePreviousBindings(hook, 'outputs', options.compareOutputsByValue, options.compareByValueDepth)
        };
        // Update bindings
        hook.bindings = hook.parser.getBindings(hook.id, hook.value, context);
        this.updateComponentWithNewOutputs(hook, context, options);
        this.updateComponentWithNewInputs(hook, options);
      }
      // If context has changed by reference, call OnDynamicChanges() for all created components.
      if (triggerOnDynamicChanges) {
        for (const hook of Object.values(hookIndex)) {
          if (typeof hook.componentRef.instance['onDynamicChanges'] === 'function') {
            hook.componentRef.instance['onDynamicChanges']({
              context
            });
          }
        }
      }
    }
    /**
     * Creates a list of all previous bindings along with their stringified values
     *
     * @param hook - The hook to check
     * @param type - The type of bindings that should be saved
     * @param saveStringified - Whether to save the stringified value in addition to the reference
     * @param stringifyDepth - How many levels deep to stringify the previous bindings
     */
    savePreviousBindings(hook, type, saveStringified, stringifyDepth) {
      const result = {};
      if (hook.bindings.hasOwnProperty(type)) {
        for (const [bindingName, bindingValue] of Object.entries(hook.bindings[type])) {
          result[bindingName] = {
            reference: bindingValue,
            stringified: saveStringified ? this.deepComparer.detailedStringify(bindingValue, stringifyDepth) : null // To compare by value
          };
        }
      }
      return result;
    }
    // Updating bindings
    // ----------------------------------------------------------------------------------------------------------------
    /**
     * Processes a hook object and (re)subscribes the outputs of a dynamic component where required
     *
     * @param hook - The hook in question
     * @param context - The current context object
     * @param options - The current HookComponentOptions
     */
    updateComponentWithNewOutputs(hook, context, options) {
      // Find out which outputs have changed
      const changedOutputs = this.getChangedBindings(hook, 'outputs', options.compareOutputsByValue, options.compareByValueDepth);
      // Check if outputs exist on component
      const existingOutputs = {};
      if (options.acceptOutputsForAnyObservable) {
        for (const [outputName, outputCallback] of Object.entries(changedOutputs)) {
          if (hook.componentRef.instance[outputName] instanceof Observable) {
            existingOutputs[outputName] = outputCallback;
          }
        }
      } else {
        const compFactory = this.cfr.resolveComponentFactory(hook.componentRef.componentType);
        for (const [outputName, outputCallback] of Object.entries(changedOutputs)) {
          const outputEntry = compFactory.outputs.filter(outputObject => outputName === (options.ignoreOutputAliases ? outputObject.propName : outputObject.templateName));
          if (outputEntry.length > 0) {
            // Save in existingInputs with actual property name, not alias
            existingOutputs[outputEntry[0].propName] = outputCallback;
          }
        }
      }
      // (Re)subscribe to outputs, store subscription in Hook
      for (const [outputName, outputCallback] of Object.entries(existingOutputs)) {
        if (hook.outputSubscriptions[outputName]) {
          hook.outputSubscriptions[outputName].unsubscribe();
        }
        hook.outputSubscriptions[outputName] = hook.componentRef.instance[outputName].subscribe(event => outputCallback(event, context));
      }
    }
    /**
     * Processes a hook object and updates the inputs of a dynamic component where required
     *
     * @param hook - The hook in question
     * @param options - The current HookComponentOptions
     */
    updateComponentWithNewInputs(hook, options) {
      // Find out which inputs have changed
      const changedInputs = this.getChangedBindings(hook, 'inputs', options.compareInputsByValue, options.compareByValueDepth);
      // Check if inputs exists on component
      const existingInputs = {};
      if (options.acceptInputsForAnyProperty) {
        for (const [inputName, inputValue] of Object.entries(changedInputs)) {
          // Even this setting has limits. Don't allow setting fundamental JavaScript object properties.
          if (!['__proto__', 'prototype', 'constructor'].includes(inputName)) {
            existingInputs[inputName] = inputValue;
          } else {
            console.error('Tried to overwrite a __proto__, prototype or constructor property with input "' + inputName + '" for hook "' + hook.componentRef.componentType.name + '". This is not allowed.');
            continue;
          }
        }
      } else {
        const compFactory = this.cfr.resolveComponentFactory(hook.componentRef.componentType);
        for (const [inputName, inputValue] of Object.entries(changedInputs)) {
          const inputEntry = compFactory.inputs.filter(inputObject => inputName === (options.ignoreInputAliases ? inputObject.propName : inputObject.templateName));
          if (inputEntry.length > 0) {
            // Save in existingInputs with actual property name, not alias
            existingInputs[inputEntry[0].propName] = inputValue;
          }
        }
      }
      // Pass in Inputs, create SimpleChanges object
      const simpleChanges = {};
      for (const [inputName, inputValue] of Object.entries(existingInputs)) {
        hook.componentRef.instance[inputName] = inputValue;
        const previousValue = hook.previousBindings && hook.previousBindings.inputs.hasOwnProperty(inputName) ? hook.previousBindings.inputs[inputName].reference : undefined;
        simpleChanges[inputName] = new SimpleChange(previousValue, inputValue, !hook.dirtyInputs.has(inputName));
        hook.dirtyInputs.add(inputName);
      }
      // Call ngOnChanges()
      if (Object.keys(simpleChanges).length > 0 && typeof hook.componentRef.instance['ngOnChanges'] === 'function') {
        hook.componentRef.instance.ngOnChanges(simpleChanges);
      }
    }
    /**
     * Compares hookData with prevHookData and finds all bindings that have changed
     *
     * @param hook - The hook in question
     * @param type - What kind of binding to check
     * @param compareByValue - Whether to compare by reference or value
     */
    getChangedBindings(hook, type, compareByValue, compareDepth) {
      const changedBindings = {};
      if (hook.bindings.hasOwnProperty(type)) {
        for (const [key, binding] of Object.entries(hook.bindings[type])) {
          // If binding did not exist in previous hook data, binding is considered changed
          if (!hook.previousBindings || !hook.previousBindings[type].hasOwnProperty(key)) {
            changedBindings[key] = binding;
            continue;
          }
          // Compare old with new
          // a) By reference
          if (!compareByValue) {
            if (binding !== hook.previousBindings[type][key].reference) {
              changedBindings[key] = binding;
            }
            // b) By value
          } else {
            const stringifiedBinding = this.deepComparer.detailedStringify(binding, compareDepth);
            const canBeComparedByValue = this.checkDetailedStringifyResultPair(key, hook.componentRef.componentType.name, compareDepth, hook.previousBindings[type][key].stringified, stringifiedBinding);
            if (canBeComparedByValue) {
              if (stringifiedBinding.result !== hook.previousBindings[type][key].stringified.result) {
                changedBindings[key] = binding;
              }
            } else {
              if (binding !== hook.previousBindings[type][key].reference) {
                changedBindings[key] = binding;
              }
            }
          }
        }
      }
      return changedBindings;
    }
    /**
     * Checks whether two detailedStringifiedResults can be compared and throws lots of errors and warnings if not
     *
     * @param bindingName - The binding in question
     * @param componentName - The component in question
     * @param compareDepth - The current compareDepth
     * @param oldResult - The detailedStringifiedResult for the old value
     * @param newResult - The detailedStringifiedResult for the new value
     */
    checkDetailedStringifyResultPair(bindingName, componentName, compareDepth, oldResult, newResult) {
      // Stringify successful?
      if (oldResult.result === null && newResult.result === null) {
        if (isDevMode()) {
          console.warn('Could stringify neither new nor old value for hook binding "' + bindingName + '" for component "' + componentName + '" to compare by value. Defaulting to comparison by reference instead.');
          return false;
        }
      }
      if (oldResult.result === null) {
        if (isDevMode()) {
          console.warn('Could not stringify old value for hook binding "' + bindingName + '" for component "' + componentName + '" to compare by value. Defaulting to comparison by reference instead.');
          return false;
        }
      }
      if (newResult.result === null) {
        if (isDevMode()) {
          console.warn('Could not stringify new value for hook binding "' + bindingName + '" for component "' + componentName + '" to compare by value. Defaulting to comparison by reference instead.');
          return false;
        }
      }
      // Max depth reached?
      if (oldResult.depthReachedCount > 0 && newResult.depthReachedCount > 0) {
        if (isDevMode()) {
          console.warn('Maximum compareByValueDepth of ' + compareDepth + ' reached ' + newResult.depthReachedCount + ' time(s) for new value and ' + oldResult.depthReachedCount + ' time(s) for old value while comparing binding "' + bindingName + '" for component "' + componentName + '.\n', 'If this impacts performance, consider simplifying this binding, reducing comparison depth or setting compareInputsByValue/compareOutputsByValue to false.');
        }
      } else if (oldResult.depthReachedCount > 0) {
        if (isDevMode()) {
          console.warn('Maximum compareByValueDepth of ' + compareDepth + ' reached ' + oldResult.depthReachedCount + ' time(s) for old value while comparing binding "' + bindingName + '" for component "' + componentName + '.\n', 'If this impacts performance, consider simplifying this binding, reducing comparison depth or setting compareInputsByValue/compareOutputsByValue to false.');
        }
      } else if (newResult.depthReachedCount > 0) {
        if (isDevMode()) {
          console.warn('Maximum compareByValueDepth of ' + compareDepth + ' reached ' + newResult.depthReachedCount + ' time(s) for new value while comparing binding "' + bindingName + '" for component "' + componentName + '.\n', 'If this impacts performance, consider simplifying this binding, reducing comparison depth or setting compareInputsByValue/compareOutputsByValue to false.');
        }
      }
      return true;
    }
  }
  ComponentUpdater.ɵfac = function ComponentUpdater_Factory(t) {
    return new (t || ComponentUpdater)(i0.ɵɵinject(i0.ComponentFactoryResolver), i0.ɵɵinject(DeepComparer));
  };
  ComponentUpdater.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: ComponentUpdater,
    factory: ComponentUpdater.ɵfac
  });
  return ComponentUpdater;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * The service responsible for dynamically creating components for all found Hooks
 */
let ComponentCreator = /*#__PURE__*/(() => {
  class ComponentCreator {
    constructor(cfr, appRef, rendererFactory, componentUpdater, platform) {
      this.cfr = cfr;
      this.appRef = appRef;
      this.rendererFactory = rendererFactory;
      this.componentUpdater = componentUpdater;
      this.platform = platform;
      this.renderer = this.rendererFactory.createRenderer(null, null);
    }
    /**
     * The main entry function to start the dynamic component initialization process
     *
     * @param contentElement - The container element with the component selector tags in its innerHTML
     * @param hookIndex - The current hookIndex (ids must match component selector ids)
     * @param token - The token used for parsetoken-attribute of the component selectors
     * @param context - The current context object
     * @param options - The current HookComponentOptions
     * @param injector - The injector to use for the dynamically-created components
     */
    init(contentElement, hookIndex, token, context, options, injector) {
      const allComponentsLoaded = new ReplaySubject(1);
      const componentLoadSubjects = [];
      const hookHostElements = {};
      // Get HookData, replace placeholders and create desired content slots
      for (const [hookId, hook] of Object.entries(hookIndex)) {
        const placeholderElement = this.platform.findPlaceholderElement(contentElement, token, hookId);
        if (placeholderElement) {
          hook.data = hook.parser.loadComponent(hook.id, hook.value, context, this.platform.getChildNodes(placeholderElement));
          // Replace placeholder element with component selector element (or a generic anchor element if lazy-loaded)
          hookHostElements[hookId] = this.replacePlaceholderElement(placeholderElement, hook);
          // Also replace child nodes with all desired ng-content slots
          // Note: Doing this immediately after the evaluation of each hook so that succeeding hooks that are
          // removed by the previous' hooks ng-content don't even need to be evaluated (let alone loaded below)
          this.createContentSlotElements(hookHostElements[hookId], hook, token);
        } else {
          // If removed by previous hook in loop via ng-content replacement
          delete hookIndex[hook.id];
        }
      }
      // Load all components from hookIndex into the prepared host elements
      for (const [hookId, hook] of Object.entries(hookIndex)) {
        // Start with of(true) to catch errors from loadComponentClass in the observable stream as well
        componentLoadSubjects.push(of(true)
        // Load component class first (might be lazy-loaded)
        .pipe(mergeMap(() => this.loadComponentClass(hook.data.component))).pipe(tap(compClass => {
          // Check if the host element is simply an anchor for a lazily-loaded component. If so, insert proper selector now.
          hookHostElements[hookId] = this.handleAnchorElement(hookHostElements[hookId], compClass);
          // Get projectableNodes from the content slots
          const projectableNodes = this.getContentSlotElements(hookHostElements[hookId], token);
          // Instantiate component
          this.createComponent(hook, context, hookHostElements[hookId], projectableNodes, options, compClass, injector);
        }))
        // If could not be created, remove from hookIndex
        .pipe(catchError(e => {
          if (isDevMode()) {
            console.error(e.stack);
          }
          delete hookIndex[hook.id];
          return of(null);
        })));
      }
      // If no components in text, no need to progress further
      if (componentLoadSubjects.length === 0) {
        allComponentsLoaded.next(true);
        return allComponentsLoaded;
      }
      // Once all normal and lazy components have loaded
      combineLatest([...componentLoadSubjects]).pipe(first()).subscribe(() => {
        // Call dynamic lifecycle methods for all created components
        for (const hook of Object.values(hookIndex)) {
          // Find all content children components
          const contentChildren = [];
          if (typeof hook.componentRef.instance['onDynamicMount'] === 'function' || typeof hook.componentRef.instance['onDynamicChanges'] === 'function') {
            this.findContentChildren(hook.componentRef.location.nativeElement, contentChildren, hookIndex, token);
          }
          // OnDynamicChanges
          if (typeof hook.componentRef.instance['onDynamicChanges'] === 'function') {
            hook.componentRef.instance['onDynamicChanges']({
              contentChildren
            });
          }
          // OnDynamicMount
          if (typeof hook.componentRef.instance['onDynamicMount'] === 'function') {
            hook.componentRef.instance['onDynamicMount']({
              context,
              contentChildren
            });
          }
        }
        // Remove now redundant attributes from component elements
        for (const hostElement of Object.values(hookHostElements)) {
          this.renderer.removeAttribute(hostElement, 'hookid');
          this.renderer.removeAttribute(hostElement, 'parsetoken');
          this.renderer.removeAttribute(hostElement, 'parser');
          this.renderer.removeAttribute(hostElement, 'ng-version');
        }
        // Done!
        allComponentsLoaded.next(true);
      });
      return allComponentsLoaded;
    }
    // DOM manipulation
    // ----------------------------------------------------------------------------------------------------------------
    /**
     * Replaces a placeholder element created in HooksReplacer with the actual component selector elements.
     * Note: If a component is being lazily-loaded, replace with generic anchor elements for now (see handleAnchorElements())
     *
     * @param placeholderElement - The placeholder to replace
     * @param hook - The corresponding hook object
     */
    replacePlaceholderElement(placeholderElement, hook) {
      let selector;
      if (hook.data.component.hasOwnProperty('prototype')) {
        selector = this.cfr.resolveComponentFactory(hook.data.component).selector;
      } else {
        selector = 'dynamic-component-anchor';
      }
      const hostElement = this.renderer.createElement(selector);
      this.renderer.setAttribute(hostElement, 'hookid', this.platform.getAttribute(placeholderElement, 'hookid'));
      this.renderer.setAttribute(hostElement, 'parsetoken', this.platform.getAttribute(placeholderElement, 'parsetoken'));
      if (this.platform.getAttribute(placeholderElement, 'parser')) {
        this.renderer.setAttribute(hostElement, 'parser', this.platform.getAttribute(placeholderElement, 'parser'));
      }
      const childNodes = this.platform.getChildNodes(placeholderElement);
      for (const node of childNodes) {
        this.renderer.appendChild(hostElement, node);
      }
      this.renderer.insertBefore(this.platform.getParentNode(placeholderElement), hostElement, placeholderElement);
      this.platform.removeChild(this.platform.getParentNode(placeholderElement), placeholderElement);
      return hostElement;
    }
    /**
     * Once the component class has been loaded, check if the targeted host element is simply a generic anchor for a lazily-loaded component.
     * If so, insert real component selector into anchor and use that as the actual host element.
     *
     * Explanation: Anchors are necessary for lazily-loaded components b/c we can't know the selector before loading the component class, so
     * a placeholder (anchor) is inserted in the meantime.
     *
     * When the component class has been loaded, we can't safely replace that placeholder anymore with the real selector element, however, due
     * to how componentFactory.create() works. If a parent component has been handed a child component placeholder node in projectableNodes on creation,
     * but doesn't render it right away (due to *ngIf, for example), you have no way to replace that placeholder anymore as it exists in a limbo and Angular
     * will simply render the node references it was given in componentFactory.create() once the parent component renders its children.
     *
     * Due to this, the selector elements of lazily-loaded components are instead simply inserted into the placeholders/anchors as children when
     * they are ready.
     *
     * @param componentHostElement - The preliminary host element which might be an anchor
     * @param compClass - The loaded component class
     */
    handleAnchorElement(componentHostElement, compClass) {
      if (this.platform.getTagName(componentHostElement) === 'DYNAMIC-COMPONENT-ANCHOR') {
        const selector = this.cfr.resolveComponentFactory(compClass).selector;
        const selectorElement = this.renderer.createElement(selector);
        // Move attributes to selector
        this.renderer.setAttribute(selectorElement, 'hookid', this.platform.getAttribute(componentHostElement, 'hookid'));
        this.renderer.setAttribute(selectorElement, 'parsetoken', this.platform.getAttribute(componentHostElement, 'parsetoken'));
        if (this.platform.getAttribute(componentHostElement, 'parser')) {
          this.renderer.setAttribute(selectorElement, 'parser', this.platform.getAttribute(componentHostElement, 'parser'));
        }
        this.renderer.removeAttribute(componentHostElement, 'hookid');
        this.renderer.removeAttribute(componentHostElement, 'parsetoken');
        this.renderer.removeAttribute(componentHostElement, 'parser');
        // Move child nodes to selector
        const childNodes = this.platform.getChildNodes(componentHostElement);
        for (const node of childNodes) {
          this.renderer.appendChild(selectorElement, node);
        }
        // Add selector name as class to anchor (for easier identification via css and such)
        this.renderer.addClass(componentHostElement, selector + '-anchor');
        // Insert
        this.renderer.appendChild(componentHostElement, selectorElement);
        componentHostElement = selectorElement;
      }
      return componentHostElement;
    }
    /**
     * Creates a content slot dom element for each ng-content outlet of the dynamically loaded component.
     *
     * This is to create a direct dom-representation of each entry in the projectableNodes array returned
     * by parser.loadComponent, so it can be cleanly resolved back into projectableNodes later on. Without these
     * content slots for seperation, you wouldn't know which child nodes go into which ng-content slot.
     *
     * @param hostElement - The dom element to create the content slots in
     * @param hook - The hook of the component
     * @param token - The current parse token
     */
    createContentSlotElements(hostElement, hook, token) {
      let content;
      // If content property is defined, use the submitted content slots
      if (hook.data.hasOwnProperty('content') && Array.isArray(hook.data.content)) {
        content = hook.data.content;
        // Otherwise just wrap existing content into single content slot
      } else {
        content = [this.platform.getChildNodes(hostElement)];
      }
      // Empty child nodes
      this.platform.clearChildNodes(hostElement);
      // Insert new ones
      let slotIndex = 0;
      for (const contentSlot of content) {
        if (contentSlot !== undefined && contentSlot !== null) {
          const contentSlotElement = this.renderer.createElement('dynamic-component-contentslot');
          this.renderer.setAttribute(contentSlotElement, 'slotIndex', slotIndex.toString());
          this.renderer.setAttribute(contentSlotElement, 'parsetoken', token);
          for (const node of contentSlot) {
            this.renderer.appendChild(contentSlotElement, node);
          }
          this.renderer.appendChild(hostElement, contentSlotElement);
        }
        slotIndex++;
      }
    }
    /**
     * Returns all previously created content slots for a component element as a projectableNodes[][] array
     *
     * @param componentHostElement - The dom element with the content slots
     * @param token - The current parse token
     */
    getContentSlotElements(componentHostElement, token) {
      // Resolve ng-content from content slots
      const projectableNodes = [];
      const contentSlotElements = this.platform.getChildNodes(componentHostElement).filter(entry => this.platform.getTagName(entry) === 'DYNAMIC-COMPONENT-CONTENTSLOT' && this.platform.getAttribute(entry, 'parsetoken') === token);
      for (const contentSlotElement of contentSlotElements) {
        const slotIndex = this.platform.getAttribute(contentSlotElement, 'slotIndex');
        projectableNodes[slotIndex] = this.platform.getChildNodes(contentSlotElement);
      }
      return projectableNodes;
    }
    // Component creation
    // ----------------------------------------------------------------------------------------------------------------
    /**
     * Takes a hook along with a DOM node and loads the specified component class (normal or lazy-loaded).
     * Returns a subject the emits the component class when ready.
     *
     * @param componentConfig - The componentConfig from HookData
     */
    loadComponentClass(componentConfig) {
      const componentClassLoaded = new ReplaySubject(1);
      // a) If is normal class
      if (componentConfig.hasOwnProperty('prototype')) {
        componentClassLoaded.next(componentConfig);
        // b) If is LazyLoadComponentConfig
      } else if (componentConfig.hasOwnProperty('importPromise') && componentConfig.hasOwnProperty('importName')) {
        // Catch typical importPromise error
        if (componentConfig.importPromise instanceof Promise) {
          throw Error(`When lazy-loading a component, the "importPromise"-field must contain a function returning the import-promise, but it contained the promise itself.`);
        }
        // Warning if using old Angular version
        const ngVersion = this.platform.getNgVersion();
        if (ngVersion < 9 && isDevMode()) {
          console.warn('It seems you are trying to use lazy-loaded-components with an Angular version older than 9. Please note that this functionality requires the new Ivy renderer to be enabled.');
        }
        componentConfig.importPromise().then(m => {
          const importName = componentConfig.importName;
          const compClass = m.hasOwnProperty(importName) ? m[importName] : m['default'];
          componentClassLoaded.next(compClass);
        });
      } else {
        throw Error('The "component" property of a returned HookData object must either contain the component class or a LazyLoadComponentConfig');
      }
      return componentClassLoaded;
    }
    /**
     * Dynamically creates a component in the specified componentHostElement
     *
     * @param hook - The hook for this component
     * @param context - The current context
     * @param componentHostElement - The hostElement for the component
     * @param projectableNodes - The nodes to inject as ng-content
     * @param options - The current HookComponentOptions
     * @param compClass - The component's class
     * @param injector - The default injector to use for the component
     */
    createComponent(hook, context, componentHostElement, projectableNodes, options, compClass, injector) {
      // Dynamically create component
      // Note: Transcluded content (including components) for ng-content can simply be added here in the form of the projectableNodes-argument.
      // The order of component creation or injection via projectableNodes does not seem to matter.
      const dynamicComponentFactory = this.cfr.resolveComponentFactory(compClass);
      const chosenInjector = hook.data.injector || injector;
      const dynamicComponentRef = dynamicComponentFactory.create(chosenInjector, projectableNodes, componentHostElement);
      // Track component
      hook.componentRef = dynamicComponentRef;
      // Set initial bindings
      hook.bindings = hook.parser.getBindings(hook.id, hook.value, context);
      this.componentUpdater.updateComponentWithNewOutputs(hook, context, options);
      this.componentUpdater.updateComponentWithNewInputs(hook, options);
      // Call initial OnDynamicChanges with context (if not undefined)
      if (typeof hook.componentRef.instance['onDynamicChanges'] === 'function' && context !== undefined) {
        hook.componentRef.instance['onDynamicChanges']({
          context
        });
      }
      // Activate change detection
      this.appRef.attachView(dynamicComponentRef.hostView);
      // Trigger an Initial cd call to:
      // - have Angular automatically invoke ngOnInit(), which happens the first time the change detector runs for a component
      // - prevent ExpressionHasChangedErrors in Angular<8
      dynamicComponentRef.changeDetectorRef.detectChanges();
    }
    // After component creation
    // ----------------------------------------------------------------------------------------------------------------
    /**
     * Find all components that would be the ContentChildren of a dynamic component and returns them in a hierarchical tree object
     * Important: This function depends on the component selector attributes 'parsetoken' and 'hookid' not being removed yet
     *
     * @param node - The HTML node to parse
     * @param treeLevel - The current tree level of DynamicContentChildren (for recursiveness)
     * @param hookIndex - The current hookIndex
     * @param token - The current parseToken
     */
    findContentChildren(node, treeLevel = [], hookIndex, token) {
      const childNodes = this.platform.getChildNodes(node);
      if (childNodes != undefined && childNodes.length > 0) {
        childNodes.forEach((childNode, key) => {
          let componentFound = false;
          // If element has a parsetoken and hookid, it is a dynamic component
          const parseToken = this.platform.getAttribute(childNode, 'parsetoken');
          if (parseToken !== null && parseToken === token && this.platform.getAttribute(childNode, 'hookid')) {
            const hookId = parseInt(this.platform.getAttribute(childNode, 'hookid'), 10);
            if (hookIndex.hasOwnProperty(hookId)) {
              treeLevel.push({
                componentRef: hookIndex[hookId].componentRef,
                componentSelector: this.platform.getTagName(childNode).toLowerCase(),
                contentChildren: [],
                hookValue: hookIndex[hookId].value
              });
              componentFound = true;
            }
          }
          // The hierarchical structure of the result is solely built on found components. It DOES NOT reflect the actual HTML structure.
          // E.g. two components returned on the same array level in the result may be on completely different nesting levels in the HTML,
          // as the only reason to 'go a level deeper' in the result is when a component was found.
          const treeLevelForNested = componentFound ? treeLevel[treeLevel.length - 1].contentChildren : treeLevel;
          this.findContentChildren(childNode, treeLevelForNested, hookIndex, token);
        });
      }
    }
  }
  ComponentCreator.ɵfac = function ComponentCreator_Factory(t) {
    return new (t || ComponentCreator)(i0.ɵɵinject(i0.ComponentFactoryResolver), i0.ɵɵinject(i0.ApplicationRef), i0.ɵɵinject(i0.RendererFactory2), i0.ɵɵinject(ComponentUpdater), i0.ɵɵinject(PlatformService));
  };
  ComponentCreator.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: ComponentCreator,
    factory: ComponentCreator.ɵfac
  });
  return ComponentCreator;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Serves as a programmatic layer of abstraction of the functionality used in OutletComponent, so that its
 * functionality can also be used without actually inserting the <ngx-dynamic-hooks>-component
 */
let OutletService = /*#__PURE__*/(() => {
  class OutletService {
    constructor(allSettings, ancestorSettings, moduleSettings, parserEntryResolver, optionsResolver, hooksReplacer, componentCreator, rendererFactory, injector) {
      this.allSettings = allSettings;
      this.ancestorSettings = ancestorSettings;
      this.moduleSettings = moduleSettings;
      this.parserEntryResolver = parserEntryResolver;
      this.optionsResolver = optionsResolver;
      this.hooksReplacer = hooksReplacer;
      this.componentCreator = componentCreator;
      this.rendererFactory = rendererFactory;
      this.injector = injector;
      this.renderer = rendererFactory.createRenderer(null, null);
    }
    /**
     * Parses a string of content and loads components for all found hooks
     *
     * @param content - The input content to parse
     * @param context - An optional context object
     * @param globalParsersBlacklist - An optional list of global parsers to blacklist
     * @param globalParsersWhitelist - An optional list of global parsers to whitelist
     * @param parsers - An optional list of parsers to use instead of the global ones
     * @param options - An optional list of options to use instead of the global ones
     * @param targetElement - An optional HTML element to use as the container for the loaded content. If none is provided, one is created and returned for you.
     * @param targetHookIndex - An optional object to fill with the programmatic hook data. If none is provided, one is created and returned for you.
     * @param injector - An optional injector to use for the dynamically-loaded components. If none is provided, the injector of the module this library is imported to is used.
     */
    parse(content, context = {}, globalParsersBlacklist = null, globalParsersWhitelist = null, parsers = null, options = null, targetElement = null, targetHookIndex = null, injector = null) {
      // If no container element or hookIndex given, create them
      if (targetElement === null) {
        targetElement = this.renderer.createElement('div');
      }
      if (targetHookIndex === null) {
        targetHookIndex = {};
      }
      // Resolve options and parsers
      this.resolveSettings();
      const resolvedOptions = this.loadOptions(options);
      const resolvedParsers = this.loadParsers(parsers, injector || this.injector, globalParsersBlacklist, globalParsersWhitelist);
      // Needs a content string
      if (!content || typeof content !== 'string') {
        return of({
          element: targetElement,
          hookIndex: targetHookIndex,
          resolvedParsers,
          resolvedOptions
        });
      }
      // Convert input HTML entities?
      if (resolvedOptions.convertHTMLEntities) {
        content = this.hooksReplacer.convertHTMLEntities(content);
      }
      // Replace hooks with component selector elements
      const token = Math.random().toString(36).substr(2, 10);
      const result = this.hooksReplacer.replaceHooksWithNodes(content, context, resolvedParsers, token, resolvedOptions, targetHookIndex);
      content = result.content;
      // Parse HTML
      targetElement.innerHTML = content;
      // Dynamically create components in component selector elements
      return this.componentCreator.init(targetElement, targetHookIndex, token, context, resolvedOptions, injector || this.injector).pipe(first()).pipe(map(allComponentsLoaded => {
        // Everything done! Return finished hookIndex and resolved parsers and options
        return {
          element: targetElement,
          hookIndex: targetHookIndex,
          resolvedParsers,
          resolvedOptions
        };
      }));
    }
    /**
     * Cleanly destroys all loaded components in a given HookIndex
     *
     * @param hookIndex - The hookIndex to process
     */
    destroy(hookIndex) {
      if (hookIndex) {
        // Destroy dynamic components
        for (const hookIndexEntry of Object.values(hookIndex)) {
          if (hookIndexEntry.componentRef) {
            hookIndexEntry.componentRef.destroy();
          }
        }
        // Unsubscribe from hook outputs
        for (const hook of Object.values(hookIndex)) {
          for (const sub of Object.values(hook.outputSubscriptions)) {
            if (sub) {
              sub.unsubscribe();
            }
          }
        }
      }
    }
    // ----------------------------------------------------------------------------------------------------------
    resolveSettings() {
      if (!this.moduleSettings.hasOwnProperty('lazyInheritance') || this.moduleSettings.lazyInheritance === DynamicHooksInheritance.All) {
        // Make sure the options of ancestorSettings (which include current moduleSettings as last entry) are last to be merged so that they always overwrite all others
        // This is in case other settings were added to the back of allSettings after registering this module
        const ancestorOptions = this.ancestorSettings.map(ancestorSettings => ancestorSettings.hasOwnProperty('globalOptions') ? {
          globalOptions: ancestorSettings.globalOptions
        } : {});
        const allSettings = [...this.allSettings, ...ancestorOptions];
        this.finalSettings = this.mergeSettings(allSettings);
      } else if (this.moduleSettings.lazyInheritance === DynamicHooksInheritance.Linear) {
        this.finalSettings = this.mergeSettings(this.ancestorSettings);
      } else if (this.moduleSettings.lazyInheritance === DynamicHooksInheritance.None) {
        this.finalSettings = this.moduleSettings;
      } else {
        throw new Error(`Incorrect DynamicHooks inheritance configuration. Used value "${this.moduleSettings.lazyInheritance}" which is not part of DynamicHooksInheritance enum. Only "All", "Linear" and "None" enum options are allowed`);
      }
    }
    /**
     * Merges multiple settings objects, overwriting previous ones with later ones in the provided array
     *
     * @param settingsArray - The settings objects to merge
     */
    mergeSettings(settingsArray) {
      const finalSettings = {};
      for (const settings of settingsArray) {
        // Parsers are simply all collected, not overwritten
        if (settings.hasOwnProperty('globalParsers')) {
          if (!finalSettings.hasOwnProperty('globalParsers')) {
            finalSettings.globalParsers = [];
          }
          for (const parserEntry of settings.globalParsers) {
            finalSettings.globalParsers.push(parserEntry);
          }
        }
        // Options are individually overwritten
        if (settings.hasOwnProperty('globalOptions')) {
          if (!finalSettings.hasOwnProperty('globalOptions')) {
            finalSettings.globalOptions = {};
          }
          for (const [optionKey, optionValue] of Object.entries(settings.globalOptions)) {
            finalSettings.globalOptions[optionKey] = optionValue;
          }
        }
      }
      return finalSettings;
    }
    /**
     * Loads the relevant outlet options
     */
    loadOptions(options) {
      let resolvedOptions;
      // If local
      if (options) {
        resolvedOptions = this.optionsResolver.resolve(options);
        // If global
      } else if (this.finalSettings && this.finalSettings.hasOwnProperty('globalOptions')) {
        resolvedOptions = this.optionsResolver.resolve(this.finalSettings.globalOptions);
        // If none given
      } else {
        resolvedOptions = outletOptionDefaults;
      }
      return resolvedOptions;
    }
    /**
     * Loads the relevant parser configuration
     */
    loadParsers(parsers, injector, globalParsersBlacklist, globalParsersWhitelist) {
      let resolvedParsers;
      // If local
      if (parsers) {
        resolvedParsers = this.parserEntryResolver.resolve(parsers, injector);
        // If global
      } else if (this.finalSettings && this.finalSettings.hasOwnProperty('globalParsers')) {
        resolvedParsers = this.parserEntryResolver.resolve(this.finalSettings.globalParsers, injector, globalParsersBlacklist, globalParsersWhitelist);
        // If none given
      } else {
        resolvedParsers = [];
      }
      return resolvedParsers;
    }
  }
  OutletService.ɵfac = function OutletService_Factory(t) {
    return new (t || OutletService)(i0.ɵɵinject(DYNAMICHOOKS_ALLSETTINGS, 8), i0.ɵɵinject(DYNAMICHOOKS_ANCESTORSETTINGS, 8), i0.ɵɵinject(DYNAMICHOOKS_MODULESETTINGS, 8), i0.ɵɵinject(ParserEntryResolver), i0.ɵɵinject(OptionsResolver), i0.ɵɵinject(HooksReplacer), i0.ɵɵinject(ComponentCreator), i0.ɵɵinject(i0.RendererFactory2), i0.ɵɵinject(i0.Injector));
  };
  OutletService.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: OutletService,
    factory: OutletService.ɵfac
  });
  return OutletService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * The main component of the DynamicHooksModule. Accepts a string of text and replaces all hooks inside of it with dynamically created
 * components that behave just like any other Angular component.
 *
 * Explanation in a nutshell:
 *
 *  1. A dynamic string of content is passed in as @Input() and an array of parsers is retrieved either as @Input() or from the global settings.
 *
 *  2. The content is given to all registered parsers who will find their respective hooks. The hooks are then replaced with dynamic component
 *     placeholder elements in the content string.
 *
 *  3. The content string is then parsed by the native browser HTML parser to create a DOM tree, which is then inserted as the innerHTML of the
 *     OutletComponent.
 *
 *  4. The corresponding components for each hook are dynamically loaded into the previously created placeholder elements as fully-functional
 *     Angular components via ComponentFactory.create().
 *
 *  5. Any @Inputs() & @Outputs() for the components will be registered with them and automatically updated on following change detection runs.
 */
let OutletComponent = /*#__PURE__*/(() => {
  class OutletComponent {
    // Lifecycle methods
    // ----------------------------------------------------------------------
    constructor(
    // Just needs to request injecting this to ensure that forRoot was called
    check, hostElement, outletService, componentUpdater, platform, injector) {
      this.check = check;
      this.hostElement = hostElement;
      this.outletService = outletService;
      this.componentUpdater = componentUpdater;
      this.platform = platform;
      this.injector = injector;
      this.componentsLoaded = new EventEmitter();
      this.hookIndex = {};
      this.activeOptions = outletOptionDefaults;
      this.activeParsers = [];
      this.token = Math.random().toString(36).substr(2, 10);
      this.initialized = false;
    }
    ngDoCheck() {
      // Update bindings on every change detection run?
      if (this.initialized && !this.activeOptions.updateOnPushOnly) {
        this.refresh(false);
      }
    }
    ngOnInit() {}
    ngOnChanges(changes) {
      // If text or options change, reset and parse from scratch
      if (changes.hasOwnProperty('content') || changes.hasOwnProperty('globalParsersBlacklist') || changes.hasOwnProperty('globalParsersWhitelist') || changes.hasOwnProperty('parsers') || changes.hasOwnProperty('options')) {
        this.reset();
        this.parse(this.content);
        return;
        // If only context changed, just refresh hook inputs/outputs
      } else if (changes.hasOwnProperty('context')) {
        this.refresh(true);
      }
    }
    ngAfterViewInit() {}
    ngAfterViewChecked() {}
    ngOnDestroy() {
      this.reset();
    }
    // ----------------------------------------------------------------------
    /**
     * Empties the state of this component
     */
    reset() {
      this.outletService.destroy(this.hookIndex);
      // Reset state
      this.platform.setInnerContent(this.hostElement.nativeElement, '');
      this.hookIndex = {};
      this.activeOptions = undefined;
      this.activeParsers = undefined;
      this.initialized = false;
    }
    /**
     * The main method of this component to initialize it
     *
     * @param content - The input content to parse
     */
    parse(content) {
      this.outletService.parse(content, this.context, this.globalParsersBlacklist, this.globalParsersWhitelist, this.parsers, this.options, this.hostElement.nativeElement, this.hookIndex, this.injector).subscribe(outletParseResult => {
        // hostElement and hookIndex are automatically filled
        this.activeParsers = outletParseResult.resolvedParsers;
        this.activeOptions = outletParseResult.resolvedOptions;
        this.initialized = true;
        // Return all loaded components
        const loadedComponents = Object.values(this.hookIndex).map(hook => {
          return {
            hookId: hook.id,
            hookValue: hook.value,
            hookParser: hook.parser,
            componentRef: hook.componentRef
          };
        });
        this.componentsLoaded.emit(loadedComponents);
      });
    }
    /**
     * Updates the bindings for all existing components
     *
     * @param triggerOnDynamicChanges - Whether to trigger the OnDynamicChanges method of dynamically loaded components
     */
    refresh(triggerOnDynamicChanges) {
      this.componentUpdater.refresh(this.hookIndex, this.context, this.activeOptions, triggerOnDynamicChanges);
    }
  }
  OutletComponent.ɵfac = function OutletComponent_Factory(t) {
    return new (t || OutletComponent)(i0.ɵɵdirectiveInject(DYNAMICHOOKS_FORROOTCHECK, 8), i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(OutletService), i0.ɵɵdirectiveInject(ComponentUpdater), i0.ɵɵdirectiveInject(PlatformService), i0.ɵɵdirectiveInject(i0.Injector));
  };
  OutletComponent.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
    type: OutletComponent,
    selectors: [["ngx-dynamic-hooks"]],
    inputs: {
      content: "content",
      context: "context",
      globalParsersBlacklist: "globalParsersBlacklist",
      globalParsersWhitelist: "globalParsersWhitelist",
      parsers: "parsers",
      options: "options"
    },
    outputs: {
      componentsLoaded: "componentsLoaded"
    },
    features: [i0.ɵɵNgOnChangesFeature],
    decls: 0,
    vars: 0,
    template: function OutletComponent_Template(rf, ctx) {},
    encapsulation: 2
  });
  return OutletComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Platform Browser Implementation of PlatformService
 */
let PlatformBrowserService = /*#__PURE__*/(() => {
  class PlatformBrowserService {
    constructor(sanitizer) {
      this.sanitizer = sanitizer;
    }
    findPlaceholderElement(contentElement, token, hookId) {
      return contentElement && contentElement.querySelector('[parsetoken="' + token + '"][hookid="' + hookId + '"]');
    }
    getAttribute(element, attributeName) {
      return element && element.getAttribute && element.getAttribute(attributeName);
    }
    getChildNodes(node) {
      return node && Array.prototype.slice.call(node.childNodes);
    }
    getTagName(element) {
      return element && element.tagName;
    }
    removeChild(parentElement, childElement) {
      if (parentElement && childElement) {
        parentElement.removeChild(childElement);
      }
    }
    clearChildNodes(element) {
      if (element) {
        while (element.firstChild) {
          element.removeChild(element.lastChild);
        }
      }
    }
    getNgVersion() {
      if (document && document.querySelector('[ng-version]')) {
        return parseInt(document.querySelector('[ng-version]').getAttribute('ng-version'), 10);
      }
      return 0;
    }
    getParentNode(element) {
      return element && element.parentNode;
    }
    setInnerContent(element, content) {
      if (element) {
        element.innerHTML = content;
      }
    }
    getInnerText(element) {
      return element && element.innerText;
    }
    sanitize(content) {
      return this.sanitizer.sanitize(SecurityContext.HTML, content);
    }
  }
  PlatformBrowserService.ɵfac = function PlatformBrowserService_Factory(t) {
    return new (t || PlatformBrowserService)(i0.ɵɵinject(i1.DomSanitizer));
  };
  PlatformBrowserService.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: PlatformBrowserService,
    factory: PlatformBrowserService.ɵfac
  });
  return PlatformBrowserService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const allSettings = [];
let DynamicHooksChildModule = /*#__PURE__*/(() => {
  class DynamicHooksChildModule {}
  DynamicHooksChildModule.ɵfac = function DynamicHooksChildModule_Factory(t) {
    return new (t || DynamicHooksChildModule)();
  };
  DynamicHooksChildModule.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
    type: DynamicHooksChildModule
  });
  DynamicHooksChildModule.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({});
  return DynamicHooksChildModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let DynamicHooksModule = /*#__PURE__*/(() => {
  class DynamicHooksModule {
    // Make sure to set the optional function signature "ModuleWithProviders<T>".
    // Note: This will break Angular 5 backwards compatibility, but enable compatibility with newer versions (13+?).
    static forRoot(rootSettings, platformService) {
      allSettings.push(rootSettings);
      return {
        ngModule: DynamicHooksModule,
        providers: [{
          provide: DYNAMICHOOKS_FORROOTCALLED,
          useValue: true
        }, {
          provide: DYNAMICHOOKS_ALLSETTINGS,
          useValue: allSettings
        }, {
          provide: DYNAMICHOOKS_MODULESETTINGS,
          useValue: rootSettings
        }, {
          provide: DYNAMICHOOKS_ANCESTORSETTINGS,
          useValue: [rootSettings]
        }, {
          provide: PlatformService,
          useClass: platformService || PlatformBrowserService
        }, OutletService, DataTypeEncoder, DataTypeParser, DeepComparer, HookFinder,
        // Internal services that are not part of public-api.ts
        OptionsResolver, ParserEntryResolver, ComponentCreator, ComponentUpdater, HooksReplacer, SelectorHookParserConfigResolver, BindingStateManager, SelectorHookFinder]
      };
    }
    static forChild(childSettings) {
      allSettings.push(childSettings);
      return {
        ngModule: DynamicHooksModule,
        providers: [{
          provide: DYNAMICHOOKS_FORROOTCHECK,
          useFactory: forRootCalled => {
            if (!forRootCalled) {
              throw new Error('It seems you\'re trying to use ngx-dynamic-hooks without calling forRoot() on the main module first. Make sure to include this to register all needed services.');
            }
            return true;
          },
          deps: [[new Optional(), DYNAMICHOOKS_FORROOTCALLED]]
        },
        // Provide the child settings
        {
          provide: DYNAMICHOOKS_MODULESETTINGS,
          useValue: childSettings
        },
        // Also add child settings to hierarchical array of child settings
        // By having itself as a dependency with SkipSelf, a circular reference is avoided as Angular will look for DYNAMICHOOKS_ANCESTORSETTINGS in the parent injector.
        // It will keep traveling injectors upwards until it finds another or just use null as the dep.
        // Also, by returning a new array reference each time, the result will only contain the direct ancestor child settings, not all child settings from every module in the app.
        // See: https://stackoverflow.com/questions/49406615/is-there-a-way-how-to-use-angular-multi-providers-from-all-multiple-levels
        {
          provide: DYNAMICHOOKS_ANCESTORSETTINGS,
          useFactory: ancestorSettings => {
            return ancestorSettings ? [...ancestorSettings, childSettings] : [childSettings];
          },
          deps: [[new SkipSelf(), new Optional(), DYNAMICHOOKS_ANCESTORSETTINGS]]
        },
        // Also provice a separate instance of OutletService for the child module (root OutletService can't see child settings)
        OutletService]
      };
    }
    static reset() {
      allSettings.length = 0;
    }
  }
  DynamicHooksModule.ɵfac = function DynamicHooksModule_Factory(t) {
    return new (t || DynamicHooksModule)();
  };
  DynamicHooksModule.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
    type: DynamicHooksModule
  });
  DynamicHooksModule.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({});
  return DynamicHooksModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * Public API Surface of ngx-dynamic-hooks
 */
// Testing
// export * from './tests/testing-api';

/**
 * Generated bundle index. Do not edit.
 */

export { DataTypeEncoder, DataTypeParser, DeepComparer, DynamicHooksChildModule, DynamicHooksInheritance, DynamicHooksModule, HookFinder, OutletComponent, OutletService, PlatformBrowserService, PlatformService, SelectorHookParser, matchAll, outletOptionDefaults, regexes, selectorHookParserConfigDefaults };
